import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PANDA_OFFICIAL_LOGO_SVG } from "../config/pandaBrand";
import { API_BASE } from "../config/api";
import {
    Search,
    ChevronsLeftRight,
    Code,
    LayoutTemplate,
    Shapes,
    Puzzle,
    Lock,
    UploadCloud,
    FolderOpen,
    Folder,
    FolderPlus,
    Image as ImageIcon,
    BarChart2,
    Crown,
    FlaskConical,
    Type,
    Video,
    MousePointerClick,
    Plus,
    Save,
    Trash2,
    Copy,
    Pencil,
    CopyPlus,
    Layers,
    ChevronDown,
    ChevronRight,
    Maximize2,
    GalleryHorizontal,
    GalleryHorizontalEnd,
    Clapperboard,
    CreditCard,
    StickyNote,
    List,
    ListTree,
    Minus,
    Quote,
    GitCommitHorizontal,
    Eye,
    Flag,
    HelpCircle,
    Gauge,
    Menu,
    PanelTop,
    PanelBottom,
    LogIn,
    Hash,
    Timer,
    ShoppingBag,
    GripVertical,
    ArrowRightToLine,
    FolderInput,
    Users,
    UserCircle,
    UserPlus,
    Mail,
    Loader2,
    RefreshCw,
    TrendingUp,
    Construction,
    Rocket,
    Share2,
    MessageCircle,
    ArrowRight,
    MoreVertical,
    Wand2,
    Gift,
    Asterisk,
    Zap,
    ShieldCheck,
    PlusCircle,
    Calendar,
    User,
    FileText,
    Table,
    LayoutGrid,
    ExternalLink,
    SeparatorVertical,
    Anchor,
    PieChart,
    Workflow,
    Wallet,
} from "lucide-react";
import { ATM_FH, ATM_ACTIVE_USER_AVATAR } from "../config/atmosphere.js";
import TemplatesGalleryModal from "./TemplatesGalleryModal";
import {
    ALL_ELEMENTS,
    ALL_GRIDS,
    getMergedElementTiers,
    getMergedGridTiers,
} from '../config/planFeatures';
import { setGepettoMediaDragData } from '../utils/gepettoMediaDrag';
import { isSiteOnlyWidget, useDeliveryMode } from '../utils/deliveryMode';
import LegalFooter from './LegalFooter';
import MediaGalleryModal from './MediaGalleryModal';

const API = API_BASE;

export function SidebarItem({
    icon,
    label,
    isActive,
    onClick,
    tier = 'basic',
    showUpdateDot = false,
    /** Plano não cobre: visual inativo; o pai pode tratar o clique (ex.: toast) em `onClick`. */
    locked = false,
    lockedTitle,
}) {
    return (
        <button
            type="button"
            title={locked ? (lockedTitle || label) : undefined}
            onClick={onClick}
            className={`relative flex flex-col items-center justify-center w-full rounded-xl py-2 gap-1 transition-all duration-200 group
        ${locked
                ? 'opacity-45 cursor-pointer text-slate-400 dark:text-slate-500'
                : isActive
                    ? "text-blue-600 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-900/40"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50"}
      `}
        >
            {isActive && (
                <div className="absolute left-[-8px] top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-600 rounded-r-full" />
            )}
            <div className="relative">
                {icon}
                {showUpdateDot && (
                    <span
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-800"
                        title=""
                        aria-hidden
                    />
                )}
                {tier === 'premium' && (
                    <div className="absolute -top-2 -right-2 text-amber-500">
                        <Crown size={12} fill="currentColor" />
                    </div>
                )}
                {tier === 'expert' && (
                    <div className="absolute -top-2 -right-2 text-violet-500">
                        <Wand2 size={12} />
                    </div>
                )}
            </div>
            <span className="text-[10px] font-medium tracking-wide">{label}</span>
        </button>
    );
}


const GRID_VISUALS = {
    '1/1': { label: "100%", cols: ["col-span-12"], classes: ["col-1-1"] },
    '1/2': { label: "50% / 50%", cols: ["col-span-6", "col-span-6"], classes: ["col-1-2", "col-1-2"] },
    '1/3': { label: "33% / 33% / 33%", cols: ["col-span-4", "col-span-4", "col-span-4"], classes: ["col-1-3", "col-1-3", "col-1-3"] },
    '1/4': { label: "25% / 25% / 25% / 25%", cols: ["col-span-3", "col-span-3", "col-span-3", "col-span-3"], classes: ["col-1-4", "col-1-4", "col-1-4", "col-1-4"] },
    '2/3': { label: "66% / 33%", cols: ["col-span-8", "col-span-4"], classes: ["col-2-3", "col-1-3"] },
    '33/66': { label: "33% / 66%", cols: ["col-span-4", "col-span-8"], classes: ["col-1-3", "col-2-3"] },
};

export function ModelosPanel({ onSelect, featureFlags = {} }) {
    const { t } = useTranslation('builder');
    const [showDynamicModal, setShowDynamicModal] = useState(false);
    const [dynamicCols, setDynamicCols] = useState(3);
    const [dynamicPcts, setDynamicPcts] = useState([34, 33, 33]);

    const gridTierMap = useMemo(() => getMergedGridTiers(featureFlags), [featureFlags]);

    const grids = useMemo(() => ALL_GRIDS.filter((g) => g.id !== 'dynamic').map((g) => ({
        id: g.id,
        label: GRID_VISUALS[g.id]?.label || g.label,
        cols: GRID_VISUALS[g.id]?.cols || [],
        classes: GRID_VISUALS[g.id]?.classes || [],
    })), []);

    /** Palco: qualquer modelo de grid; exportação valida contra plano.grids quando a lista está configurada. */
    const isGridUnlocked = useCallback(() => true, []);

    const applyDynamicCols = (n) => {
        setDynamicCols(n);
        const equal = Math.floor(100 / n);
        const remainder = 100 - equal * n;
        const arr = Array(n).fill(equal);
        for (let i = 0; i < remainder; i++) arr[i]++;
        setDynamicPcts(arr);
    };

    const updateDynamicPct = (idx, val) => {
        const v = Math.max(0, Math.min(100, parseInt(val, 10) || 0));
        setDynamicPcts(prev => {
            const next = [...prev];
            next[idx] = v;
            return next;
        });
    };

    const totalPct = dynamicPcts.reduce((a, b) => a + b, 0);
    const isValid = totalPct === 100;

    const confirmDynamic = () => {
        if (!isValid || !isGridUnlocked('dynamic')) return;
        onSelect('dynamic', { customPercentages: [...dynamicPcts] });
        setShowDynamicModal(false);
    };

    return (
        <div className="space-y-4 px-1">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
                {t('selectGrid')}
            </p>
            <div className="grid grid-cols-1 gap-3">
                {grids.map((grid) => (
                    <button
                        key={grid.id}
                        type="button"
                        disabled={!isGridUnlocked(grid.id)}
                        onClick={() => isGridUnlocked(grid.id) && onSelect(grid.id, grid.classes)}
                        className={`group flex flex-col gap-2 p-4 border border-slate-200 rounded-lg transition-all bg-white relative ${isGridUnlocked(grid.id) ? 'hover:border-blue-500 hover:shadow-md' : 'opacity-45 cursor-not-allowed'}`}
                    >
                        <div className="absolute top-2 right-2 flex gap-1">
                            {gridTierMap[grid.id] === 'premium' && <Crown size={12} className="text-amber-500" fill="currentColor" />}
                            {gridTierMap[grid.id] === 'expert' && <Wand2 size={12} className="text-violet-500" />}
                        </div>
                        <div className="grid grid-cols-12 gap-1 w-full h-12">
                            {grid.cols.map((colClass, i) => (
                                <div
                                    key={i}
                                    className={`${colClass} bg-slate-200 group-hover:bg-blue-100 rounded-sm flex items-center justify-center transition-colors`}
                                >
                                    <span className="text-xs font-mono text-slate-400 group-hover:text-blue-400">
                                        {grid.label.split(' / ')[i]}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <span className="text-xs text-slate-600 font-medium">{grid.label}</span>
                    </button>
                ))}
                <button
                    type="button"
                    disabled={!isGridUnlocked('dynamic')}
                    onClick={() => isGridUnlocked('dynamic') && setShowDynamicModal(true)}
                    className={`group flex flex-col gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg transition-all bg-white relative ${isGridUnlocked('dynamic') ? 'hover:border-blue-500 hover:bg-blue-50/30' : 'opacity-45 cursor-not-allowed'}`}
                >
                    <div className="absolute top-2 right-2 flex gap-1">
                        {gridTierMap.dynamic === 'premium' && <Crown size={12} className="text-amber-500" fill="currentColor" />}
                        {gridTierMap.dynamic === 'expert' && <Wand2 size={12} className="text-violet-500" />}
                    </div>
                    <div className="grid grid-cols-4 gap-1 w-full h-12 items-center justify-center">
                        <div className="col-span-1 bg-slate-200 group-hover:bg-blue-100 rounded-sm h-8 flex items-center justify-center"><span className="text-xs font-mono text-slate-400">25%</span></div>
                        <div className="col-span-2 bg-slate-200 group-hover:bg-blue-100 rounded-sm h-8 flex items-center justify-center"><span className="text-xs font-mono text-slate-400">50%</span></div>
                        <div className="col-span-1 bg-slate-200 group-hover:bg-blue-100 rounded-sm h-8 flex items-center justify-center"><span className="text-xs font-mono text-slate-400">25%</span></div>
                    </div>
                    <span className="text-xs text-slate-600 font-medium">{t('builder:gridDynamic', { defaultValue: 'Grid Dinâmico' })}</span>
                </button>
            </div>

            {showDynamicModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[3000]" onClick={() => setShowDynamicModal(false)}>
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold mb-4">{t('builder:gridDynamic', { defaultValue: 'Grid Dinâmico' })}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('builder:gridDynamicHint', { defaultValue: 'Defina o número de colunas e a porcentagem de cada uma (total 100%).' })}</p>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">{t('builder:gridDynamicCols', { defaultValue: 'Número de colunas' })}</label>
                                <select className="w-full border rounded-lg px-3 py-2" value={dynamicCols} onChange={e => applyDynamicCols(parseInt(e.target.value, 10))}>
                                    {[2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">{t('builder:gridDynamicPcts', { defaultValue: 'Porcentagem por coluna' })}</label>
                                <div className="flex flex-wrap gap-2">
                                    {dynamicPcts.map((p, i) => (
                                        <div key={i} className="flex items-center gap-1">
                                            <span className="text-xs text-slate-500 dark:text-slate-400">{t('builder:gridDynamicColLabel', { defaultValue: 'Col' })} {i + 1}:</span>
                                            <input type="number" min={0} max={100} value={p} onChange={e => updateDynamicPct(i, e.target.value)} className="w-16 border rounded px-2 py-1 text-sm" />
                                            <span className="text-xs text-slate-500">%</span>
                                        </div>
                                    ))}
                                </div>
                                <p className={`text-xs mt-2 ${isValid ? 'text-green-600' : 'text-amber-600'}`}>
                                    {t('builder:gridDynamicTotal', { defaultValue: 'Total' })}: {totalPct}% {!isValid && `(${t('builder:gridDynamicMust100', { defaultValue: 'deve ser 100%' })})`}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button type="button" onClick={() => setShowDynamicModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
                                {t('builder:cancel', { defaultValue: 'Cancelar' })}
                            </button>
                            <button type="button" onClick={confirmDynamic} disabled={!isValid} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {t('builder:confirm', { defaultValue: 'Confirmar' })}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const ELEMENT_LABEL_KEYS = {
    'text': 'elText', 'title-basic': 'elTitleBasic', 'image': 'elImage', 'video': 'elVideo', 'panda-video': 'elPandaVideo', 'h5p': 'elH5p',
    'styled-button': 'elButton', 'page-break': 'elSeparator', 'list-icon': 'elListIcons',
    'tabs': 'elTabs', 'accordion': 'elAccordion', 'divider': 'elDivider',
    'styled-card': 'elStyledCard', 'alert': 'elAlert', 'section-header': 'elHeader',
    'htmlTemplate': 'elHtmlTemplate',
    'modal-trigger': 'elModal', 'carousel': 'elCarousel', 'card-slider': 'elCardSlider', 'card-shop': 'elCardShop', 'video-card-slider': 'elVideoCardSlider', 'image-card': 'elImageCard',
    'social-card': 'elSocialCard',
    'post-it': 'elPostIt', 'blockquote': 'elBlockquote', 'moodle-activity': 'elOpenMoodleActivity',
    'geppetto-backpack': 'elBackpack',
    'timeline': 'elTimeline', 'checkpoint': 'elCheckpoint', 'reinforcement-quiz': 'elReinforcementQuiz', 'progress-bar': 'elProgressBar', 'header-menu': 'elHeaderMenu', 'site-menu': 'elSiteMenu', 'site-footer': 'elSiteFooter', 'site-login': 'elSiteLogin', 'site-maintenance': 'elSiteMaintenance', 'count-up': 'elCountUp', 'countdown': 'elCountdown', 'comments': 'elComments',
    'spacer': 'elSpacer', 'responsive-table': 'elResponsiveTable', 'image-gallery': 'elImageGallery',
    'chart-widget': 'elChartWidget',
    'flowchart-widget': 'elFlowchartWidget',
    'table-of-contents': 'elTableOfContents',
    'asaas-payment': 'elAsaasPayment',
    'mercadopago-payment': 'elMercadoPagoPayment',
};

const ELEMENT_ICONS = {
    text: <Type size={18} />, 'title-basic': <Anchor size={18} />, image: <ImageIcon size={18} />, video: <Video size={18} />, 'panda-video': <img src={PANDA_OFFICIAL_LOGO_SVG} alt="" width={20} height={20} style={{ objectFit: 'contain', display: 'block' }} loading="lazy" decoding="async" />, h5p: <Puzzle size={18} />,
    'styled-button': <MousePointerClick size={18} />, 'page-break': <ChevronsLeftRight size={18} />,
    'list-icon': <LayoutTemplate size={18} />, tabs: <Layers size={18} />, accordion: <ChevronDown size={18} />,
    divider: <Minus size={18} />, htmlTemplate: <Code size={18} />,
    'styled-card': <LayoutTemplate size={18} />, alert: <LayoutTemplate size={18} />, 'section-header': <Type size={18} />,
    'modal-trigger': <Maximize2 size={18} />, carousel: <GalleryHorizontal size={18} />, 'card-slider': <GalleryHorizontalEnd size={18} />, 'card-shop': <ShoppingBag size={18} />, 'video-card-slider': <Clapperboard size={18} />, 'image-card': <CreditCard size={18} />,
    'social-card': <UserCircle size={18} />,
    'post-it': <StickyNote size={18} />, blockquote: <Quote size={18} />, 'moodle-activity': <ExternalLink size={18} />,
    'geppetto-backpack': <StickyNote size={18} />,
    timeline: <GitCommitHorizontal size={18} />, checkpoint: <Flag size={18} />, 'reinforcement-quiz': <HelpCircle size={18} />, 'progress-bar': <Gauge size={18} />, 'header-menu': <Menu size={18} />, 'site-menu': <PanelTop size={18} />, 'site-footer': <PanelBottom size={18} />, 'site-login': <LogIn size={18} />, 'site-maintenance': <Construction size={18} />, 'count-up': <Hash size={18} />, countdown: <Timer size={18} />, comments: <MessageCircle size={18} />,
    spacer: <SeparatorVertical size={18} />, 'responsive-table': <Table size={18} />, 'image-gallery': <LayoutGrid size={18} />,
    'chart-widget': <PieChart size={18} />,
    'flowchart-widget': <Workflow size={18} />,
    'table-of-contents': <ListTree size={18} />,
    'asaas-payment': <CreditCard size={18} />,
    'mercadopago-payment': <CreditCard size={18} className="text-sky-600 dark:text-sky-400" />,
};

function normalizeForSearch(s) {
    return String(s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

export function ElementosPanel({ onSelect, hasSelectedSlot, headerElements = [], featureFlags = {} }) {
    const { t } = useTranslation('builder');
    const deliveryMode = useDeliveryMode();
    const [elementSearch, setElementSearch] = useState('');
    const trialSet = new Set(featureFlags.trial || []);
    const headerSet = new Set(headerElements);
    const canAdd = (elId) => hasSelectedSlot || headerSet.has(elId);

    const tierMap = useMemo(() => getMergedElementTiers(featureFlags), [featureFlags]);

    const allElements = useMemo(() => ALL_ELEMENTS.map(e => ({
        id: e.id,
        icon: ELEMENT_ICONS[e.id],
        tier: tierMap[e.id] || 'basic',
    })).filter(e => e.icon && (deliveryMode === 'site' || !isSiteOnlyWidget(e.id))), [tierMap, deliveryMode]);

    const searchNorm = normalizeForSearch(elementSearch.trim());
    const filteredElements = useMemo(() => {
        if (!searchNorm) return allElements;
        return allElements.filter((el) => {
            const label = t(ELEMENT_LABEL_KEYS[el.id] || el.id);
            const labelNorm = normalizeForSearch(label);
            const idNorm = normalizeForSearch(el.id).replace(/-/g, ' ');
            const q = searchNorm.replace(/-/g, ' ');
            return labelNorm.includes(q) || idNorm.includes(q);
        });
    }, [allElements, elementSearch, searchNorm, t]);

    const basicEls = filteredElements.filter(e => e.tier === 'basic');
    const expertEls = filteredElements.filter(e => e.tier === 'expert');
    const premiumEls = filteredElements.filter(e => e.tier === 'premium');
    const noSearchResults = Boolean(searchNorm) && basicEls.length === 0 && expertEls.length === 0 && premiumEls.length === 0;

    const hasAnySlot = hasSelectedSlot || headerSet.size > 0;
    return (
        <div className="space-y-4 px-1">
            {!hasSelectedSlot && headerSet.size === 0 && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200 flex gap-3 items-start leading-relaxed">
                    <Shapes className="shrink-0 mt-0.5" size={16} />
                    <p>{t('selectSlotFirst')}</p>
                </div>
            )}
            {!hasSelectedSlot && headerSet.size > 0 && (
                <div className="p-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg text-xs text-sky-700 dark:text-sky-300">
                    <p>{t('headerElementsNote')}</p>
                </div>
            )}

            <div className="space-y-2">
                <label htmlFor="gepetto-element-search" className="text-xs font-semibold text-slate-600 dark:text-slate-400 block">
                    {t('searchElements')}
                </label>
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} aria-hidden />
                    <input
                        id="gepetto-element-search"
                        type="search"
                        value={elementSearch}
                        onChange={(e) => setElementSearch(e.target.value)}
                        placeholder={t('searchElementsPlaceholder')}
                        autoComplete="off"
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                    />
                </div>
            </div>

            <div
                className={`space-y-6 ${!hasAnySlot ? "opacity-50 pointer-events-none" : "mt-6"}`}
            >
                {noSearchResults && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 py-2">{t('searchElementsNoResults')}</p>
                )}
                {basicEls.length > 0 && (
                    <div>
                        <h3 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-3">
                            {t('basic')}
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            {basicEls.map(el => (
                                <ElementButton
                                    key={el.id}
                                    icon={el.icon}
                                    label={t(ELEMENT_LABEL_KEYS[el.id] || el.id)}
                                    tier="basic"
                                    isTrial={trialSet.has(el.id)}
                                    isHeader={headerSet.has(el.id)}
                                    disabled={!canAdd(el.id)}
                                    onClick={() => canAdd(el.id) && onSelect(el.id)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {expertEls.length > 0 && (
                    <div>
                        <h3 className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                            {t('expert')} <Wand2 size={12} className="text-violet-500" />
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            {expertEls.map(el => (
                                <ElementButton
                                    key={el.id}
                                    icon={el.icon}
                                    label={t(ELEMENT_LABEL_KEYS[el.id] || el.id)}
                                    tier="expert"
                                    isTrial={trialSet.has(el.id)}
                                    isHeader={headerSet.has(el.id)}
                                    disabled={!canAdd(el.id)}
                                    onClick={() => canAdd(el.id) && onSelect(el.id)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {premiumEls.length > 0 && (
                    <div>
                        <h3 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                            {t('premium')} <Crown size={12} className="text-amber-500" />
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            {premiumEls.map(el => (
                                <ElementButton
                                    key={el.id}
                                    icon={el.icon}
                                    label={t(ELEMENT_LABEL_KEYS[el.id] || el.id)}
                                    tier="premium"
                                    isTrial={trialSet.has(el.id)}
                                    isHeader={headerSet.has(el.id)}
                                    disabled={!canAdd(el.id)}
                                    onClick={() => canAdd(el.id) && onSelect(el.id)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ElementButton({ icon, label, tier = 'basic', isTrial, isHeader, disabled, onClick }) {
    const tierChrome = {
        basic: {
            border: '1px solid #93c5fd',
            boxShadow: '0 1px 4px rgba(59, 130, 246, 0.18)',
            hoverBorder: '#3b82f6',
            hoverShadow: '0 4px 12px rgba(59, 130, 246, 0.22)',
            hoverIconBg: '#3b82f6',
        },
        expert: {
            border: '1px solid #c4b5fd',
            boxShadow: '0 1px 4px rgba(124, 58, 237, 0.16)',
            hoverBorder: '#7c3aed',
            hoverShadow: '0 4px 12px rgba(124, 58, 237, 0.2)',
            hoverIconBg: '#7c3aed',
        },
        premium: {
            border: '1px solid #fcd34d',
            boxShadow: '0 1px 4px rgba(245, 158, 11, 0.2)',
            hoverBorder: '#f59e0b',
            hoverShadow: '0 4px 12px rgba(245, 158, 11, 0.22)',
            hoverIconBg: '#f59e0b',
        },
    }[tier] || {
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        hoverBorder: '#3b82f6',
        hoverShadow: '0 4px 12px rgba(99,102,241,0.15)',
        hoverIconBg: '#3b82f6',
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-element-tier={tier}
            style={{
                opacity: disabled ? 0.5 : 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '16px 8px',
                background: 'white',
                border: tierChrome.border,
                borderRadius: '10px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                position: 'relative',
                transition: 'all 0.2s ease',
                boxShadow: tierChrome.boxShadow,
                width: '100%',
            }}
            onMouseEnter={e => {
                if (e.currentTarget.disabled) return;
                e.currentTarget.style.borderColor = tierChrome.hoverBorder;
                e.currentTarget.style.boxShadow = tierChrome.hoverShadow;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.querySelector('.elem-icon-wrap').style.background = tierChrome.hoverIconBg;
                e.currentTarget.querySelector('.elem-icon-wrap').style.color = 'white';
            }}
            onMouseLeave={e => {
                if (e.currentTarget.disabled) return;
                e.currentTarget.style.border = tierChrome.border;
                e.currentTarget.style.boxShadow = tierChrome.boxShadow;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.querySelector('.elem-icon-wrap').style.background = '#f1f5f9';
                e.currentTarget.querySelector('.elem-icon-wrap').style.color = '#475569';
            }}
        >
            {isHeader && (
                <span style={{ position: 'absolute', top: '4px', left: '6px', fontSize: '9px', fontWeight: 600, color: '#0ea5e9', textTransform: 'uppercase' }}>Header</span>
            )}
            {tier === 'premium' && (
                <div style={{ position: 'absolute', top: '6px', right: '7px', color: '#f59e0b' }}>
                    <Crown size={11} fill="currentColor" />
                </div>
            )}
            {tier === 'expert' && (
                <div style={{ position: 'absolute', top: '6px', right: '7px', color: '#7c3aed' }}>
                    <Wand2 size={11} />
                </div>
            )}
            {isTrial && (
                <div style={{ position: 'absolute', top: '6px', left: '7px', color: '#3b82f6' }}>
                    <FlaskConical size={11} />
                </div>
            )}
            <div
                className="elem-icon-wrap"
                style={{
                    width: '40px', height: '40px',
                    borderRadius: '8px',
                    background: '#f1f5f9',
                    color: '#475569',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                }}
            >
                {icon}
            </div>
            <span style={{ fontSize: '11px', fontWeight: '600', color: '#334155', lineHeight: 1.2, textAlign: 'center' }}>
                {label}
            </span>
        </button>
    );
}

export function PluginPanel({ authToken, pluginsLocked = false, onOpenPlans }) {
    const { t } = useTranslation('builder');
    const [manifest, setManifest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [downloading, setDownloading] = useState(null);

    const load = useCallback(() => {
        if (!authToken || pluginsLocked) {
            setLoading(false);
            setManifest(null);
            return;
        }
        setLoading(true);
        setErr('');
        fetch(`${API_BASE}/moodle-plugins/manifest`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || t('pluginManifestError'));
                setManifest(d);
            })
            .catch((e) => setErr(e.message || String(e)))
            .finally(() => setLoading(false));
    }, [authToken, pluginsLocked, t]);

    useEffect(() => { load(); }, [load]);

    const downloadZip = (pluginId) => {
        if (!authToken) return;
        setDownloading(pluginId);
        fetch(`${API_BASE}/moodle-plugins/download/${pluginId}`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then(async (r) => {
                if (!r.ok) {
                    const j = await r.json().catch(() => ({}));
                    throw new Error(j.error || `HTTP ${r.status}`);
                }
                return r.blob();
            })
            .then((blob) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `${pluginId}.zip`;
                a.click();
                URL.revokeObjectURL(a.href);
            })
            .catch((e) => setErr(e.message || String(e)))
            .finally(() => setDownloading(null));
    };

    if (!authToken) {
        return <p className="text-sm text-slate-500 dark:text-slate-400">{t('pluginNeedLogin')}</p>;
    }

    if (pluginsLocked) {
        return (
            <div className="space-y-6">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 shadow-sm dark:border-amber-800 dark:bg-amber-950/40">
                    <h3 className="font-semibold text-amber-950 dark:text-amber-100 mb-2">{t('plugins')}</h3>
                    <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-200 mb-4">
                        {t('modulePluginLockedHint')}
                    </p>
                    {typeof onOpenPlans === 'function' ? (
                        <button
                            type="button"
                            onClick={onOpenPlans}
                            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                        >
                            {t('upgradeNow')}
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="p-5 bg-[#3b82f6] rounded-xl text-white shadow-md relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-20">
                    <Puzzle size={64} />
                </div>
                <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                        <Crown size={16} fill="currentColor" className="text-amber-300" />
                        <span className="text-xs font-bold uppercase tracking-wider text-blue-100">
                            {t('pluginPremium')}
                        </span>
                    </div>
                    <h3 className="font-semibold text-lg mb-1">{t('pluginMoodle')}</h3>
                    <p className="text-sm text-blue-100 mb-2">
                        {t('pluginDesc')}
                    </p>
                    <p className="text-xs text-blue-100/90 leading-relaxed">
                        {t('pluginZipHint')}
                    </p>
                </div>
            </div>

            {err && (
                <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                    {err}
                </div>
            )}

            {loading && <p className="text-sm text-slate-500">{t('pluginManifestLoading')}</p>}

            {!loading && manifest?.plugins && (
                <div className="space-y-4">
                    {manifest.plugins.map((p) => (
                        <div
                            key={p.id}
                            className="rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/80 p-4 shadow-sm"
                        >
                            <div className="flex flex-col gap-3">
                                <div className="min-w-0">
                                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">{p.title}</h4>
                                    {p.subtitle ? (
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{p.subtitle}</p>
                                    ) : null}
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-600 dark:text-slate-300">
                                        <span>
                                            <span className="text-slate-400 dark:text-slate-500">{t('pluginVersionLabel')}: </span>
                                            <span className="font-mono font-semibold">{p.version || '—'}</span>
                                        </span>
                                        {p.updated_at ? (
                                            <span>
                                                <span className="text-slate-400 dark:text-slate-500">{t('pluginUpdatedLabel')}: </span>
                                                {p.updated_at}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    disabled={!p.has_file || downloading === p.id}
                                    onClick={() => downloadZip(p.id)}
                                    className="self-start w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {downloading === p.id ? '…' : p.has_file ? t('pluginDownloadZip') : t('pluginZipUnavailable')}
                                </button>
                            </div>
                            {p.changelog && String(p.changelog).trim() ? (
                                <details className="mt-3 text-xs border-t border-slate-100 dark:border-slate-700 pt-3">
                                    <summary className="cursor-pointer font-semibold text-slate-600 dark:text-slate-300">
                                        {t('pluginChangelog')}
                                    </summary>
                                    <pre className="mt-2 whitespace-pre-wrap font-sans text-slate-600 dark:text-slate-400 leading-relaxed">
                                        {p.changelog}
                                    </pre>
                                </details>
                            ) : null}
                        </div>
                    ))}
                </div>
            )}

            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                {t('pluginApiMovedHint')}
            </p>
        </div>
    );
}

/** Painel «Minha API»: gera chave e trava URL do Moodle (plano com módulo myApi / regra planAllowsMyApiMenu). */
export function MyApiPanel({ authToken, onConfigurePaymentGateways, myApiLocked = false, onOpenPlans }) {
    const { t } = useTranslation('builder');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [moodleUrl, setMoodleUrl] = useState('');
    const [busy, setBusy] = useState(false);
    const [newKey, setNewKey] = useState('');

    const load = useCallback(() => {
        if (!authToken || myApiLocked) {
            setLoading(false);
            setData(null);
            return;
        }
        setLoading(true);
        fetch(`${API_BASE}/tenant/moodle-api`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || t('myApiLoadError'));
                setData(d);
                setErr('');
            })
            .catch((e) => setErr(e.message || String(e)))
            .finally(() => setLoading(false));
    }, [authToken, myApiLocked, t]);

    useEffect(() => { load(); }, [load]);

    const handleGenerate = () => {
        if (!authToken || myApiLocked) return;
        setBusy(true);
        setErr('');
        setNewKey('');
        fetch(`${API_BASE}/tenant/moodle-api/generate`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ moodle_url: moodleUrl.trim() }),
        })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || t('myApiGenError'));
                setNewKey(d.api_key || '');
                if (d.moodle_plugin_base_url) setMoodleUrl('');
                load();
            })
            .catch((e) => setErr(e.message || String(e)))
            .finally(() => setBusy(false));
    };

    const handleRegenerate = () => {
        if (!authToken || myApiLocked || !data?.locked) return;
        if (!window.confirm(t('myApiRegenConfirm'))) return;
        setBusy(true);
        setErr('');
        setNewKey('');
        fetch(`${API_BASE}/tenant/moodle-api/regenerate`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ moodle_url: data.moodle_plugin_base_url || '' }),
        })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || t('myApiRegenError'));
                setNewKey(d.api_key || '');
                load();
            })
            .catch((e) => setErr(e.message || String(e)))
            .finally(() => setBusy(false));
    };

    const inputCls = 'w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200';

    if (!authToken) {
        return <p className="text-sm text-slate-500 dark:text-slate-400">{t('myApiNeedLogin')}</p>;
    }

    if (myApiLocked) {
        return (
            <div className="space-y-6">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 shadow-sm dark:border-amber-800 dark:bg-amber-950/40">
                    <h3 className="font-semibold text-amber-950 dark:text-amber-100 mb-2">{t('myApiPanelTitle')}</h3>
                    <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-200 mb-4">
                        {t('moduleMyApiLockedHint')}
                    </p>
                    {typeof onOpenPlans === 'function' ? (
                        <button
                            type="button"
                            onClick={onOpenPlans}
                            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                        >
                            {t('upgradeNow')}
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    if (loading && !data) {
        return <p className="text-sm text-slate-500">{t('myApiLoading')}</p>;
    }

    return (
        <div className="space-y-5">
            {err && (
                <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                    {err}
                </div>
            )}

            {data?.api_suspended && (
                <div className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 rounded-lg px-3 py-2">
                    {t('myApiSuspended')}
                </div>
            )}

            <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50">
                <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200 font-semibold text-sm mb-2">
                    <Lock size={16} />
                    {t('myApiLockedUrl')}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{t('myApiLockedUrlHelp')}</p>
                <div className="font-mono text-sm break-all text-slate-700 dark:text-slate-300">
                    {data?.moodle_plugin_base_url || '—'}
                </div>
                {data?.api_key_prefix && (
                    <p className="text-xs text-slate-500 mt-2">
                        {t('myApiKeyPrefix')}: <span className="font-mono">{data.api_key_prefix}…</span>
                    </p>
                )}
                {data?.moodle_api_expires_at && (
                    <p className="text-xs text-slate-500 mt-1">{t('myApiExpires')}: {data.moodle_api_expires_at}</p>
                )}
            </div>

            <div>
                <h4 className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">{t('myApiMoodleInstallTitle')}</h4>
                <ol className="text-xs text-slate-600 dark:text-slate-400 space-y-2 list-decimal pl-4">
                    <li>{t('myApiStepKeyMoodle')}</li>
                    <li>{t('myApiStepRegen')}</li>
                </ol>
            </div>

            {!data?.locked ? (
                <div className="space-y-2">
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400">{t('myApiFirstUrlLabel')}</label>
                    <input
                        type="url"
                        value={moodleUrl}
                        onChange={(e) => setMoodleUrl(e.target.value)}
                        placeholder={t('pluginMoodleUrlPlaceholder')}
                        className={inputCls}
                    />
                    <button
                        type="button"
                        disabled={busy || data?.api_suspended}
                        onClick={handleGenerate}
                        className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                    >
                        {busy ? '…' : t('myApiGenerate')}
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('myApiRegenHint')}</p>
                    <button
                        type="button"
                        disabled={busy || data?.api_suspended}
                        onClick={handleRegenerate}
                        className="w-full py-2.5 bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                    >
                        {busy ? '…' : t('myApiRegenerateKey')}
                    </button>
                </div>
            )}

            {newKey && (
                <div className="p-4 rounded-xl border-2 border-emerald-500/50 bg-emerald-50 dark:bg-emerald-900/20">
                    <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-200 mb-2">{t('myApiCopyOnce')}</p>
                    <textarea readOnly className="w-full text-xs font-mono p-2 rounded border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-slate-900" rows={3} value={newKey} />
                    <button
                        type="button"
                        className="mt-2 text-sm text-blue-600 dark:text-blue-400 font-semibold"
                        onClick={() => navigator.clipboard.writeText(newKey)}
                    >
                        {t('myApiCopy')}
                    </button>
                </div>
            )}

            {onConfigurePaymentGateways ? (
                <div className="pt-6 mt-2 border-t border-slate-200 dark:border-slate-600">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">{t('myApiPaymentGatewaysHint')}</p>
                    <button
                        type="button"
                        onClick={onConfigurePaymentGateways}
                        className="w-full py-2.5 px-3 rounded-lg text-sm font-semibold border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700/80 flex items-center justify-center gap-2"
                    >
                        <Wallet size={16} className="text-emerald-600 dark:text-emerald-400" />
                        {t('myApiConfigureGatewaysButton')}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

/**
 * Configuração de gateways (Asaas, Mercado Pago, …) no perfil do tenant.
 * @param {{ authToken: string, initialProviderTab?: 'asaas'|'mercadopago'|'stripe'|null }} props
 */
export function PaymentGatewayPanel({ authToken, initialProviderTab = null }) {
    const { t: tb } = useTranslation('builder');
    const { t: tp } = useTranslation('profile');
    const [providerTab, setProviderTab] = useState('asaas');
    const [moodleApi, setMoodleApi] = useState(null);
    const [asaas, setAsaas] = useState(null);
    const [asaasLoading, setAsaasLoading] = useState(false);
    const [asaasErr, setAsaasErr] = useState('');
    const [asaasKey, setAsaasKey] = useState('');
    const [asaasWebhookToken, setAsaasWebhookToken] = useState('');
    const [asaasWebhookTouched, setAsaasWebhookTouched] = useState(false);
    const [asaasSandbox, setAsaasSandbox] = useState(true);
    const [asaasBusy, setAsaasBusy] = useState(false);

    const [mp, setMp] = useState(null);
    const [mpLoading, setMpLoading] = useState(false);
    const [mpErr, setMpErr] = useState('');
    const [mpToken, setMpToken] = useState('');
    const [mpWebhookSecret, setMpWebhookSecret] = useState('');
    const [mpWebhookTouched, setMpWebhookTouched] = useState(false);
    const [mpTestMode, setMpTestMode] = useState(true);
    const [mpBusy, setMpBusy] = useState(false);

    useEffect(() => {
        if (initialProviderTab === 'mercadopago' || initialProviderTab === 'asaas') {
            setProviderTab(initialProviderTab);
        }
    }, [initialProviderTab]);

    const loadMoodleApi = useCallback(() => {
        if (!authToken) return;
        fetch(`${API_BASE}/tenant/moodle-api`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (ok) setMoodleApi(d);
            })
            .catch(() => {});
    }, [authToken]);

    const loadAsaas = useCallback(() => {
        if (!authToken) return;
        setAsaasLoading(true);
        setAsaasErr('');
        fetch(`${API_BASE}/tenant/asaas-integration`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || tb('asaasIntegrationLoadError'));
                setAsaas(d);
                setAsaasSandbox(d.sandbox !== false);
            })
            .catch((e) => setAsaasErr(e.message || String(e)))
            .finally(() => setAsaasLoading(false));
    }, [authToken, tb]);

    useEffect(() => {
        loadMoodleApi();
    }, [loadMoodleApi]);

    useEffect(() => {
        loadAsaas();
    }, [loadAsaas]);

    const loadMp = useCallback(() => {
        if (!authToken) return;
        setMpLoading(true);
        setMpErr('');
        fetch(`${API_BASE}/tenant/mercadopago-integration`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || tb('mercadopagoIntegrationLoadError'));
                setMp(d);
                setMpTestMode(d.test_mode !== false);
            })
            .catch((e) => setMpErr(e.message || String(e)))
            .finally(() => setMpLoading(false));
    }, [authToken, tb]);

    useEffect(() => {
        if (providerTab === 'mercadopago') loadMp();
    }, [providerTab, loadMp]);

    const handleSaveMp = () => {
        if (!authToken) return;
        setMpBusy(true);
        setMpErr('');
        const payload = { test_mode: mpTestMode };
        if (mpToken.trim()) payload.access_token = mpToken.trim();
        if (mpWebhookTouched) payload.webhook_secret = mpWebhookSecret;
        fetch(`${API_BASE}/tenant/mercadopago-integration`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || 'Erro');
                setMpToken('');
                setMpWebhookSecret('');
                setMpWebhookTouched(false);
                loadMp();
            })
            .catch((e) => setMpErr(e.message || String(e)))
            .finally(() => setMpBusy(false));
    };

    const handleSaveAsaas = () => {
        if (!authToken) return;
        setAsaasBusy(true);
        setAsaasErr('');
        const payload = { sandbox: asaasSandbox };
        if (asaasKey.trim()) payload.api_key = asaasKey.trim();
        if (asaasWebhookTouched) payload.webhook_auth_token = asaasWebhookToken;
        fetch(`${API_BASE}/tenant/asaas-integration`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || 'Erro');
                setAsaasKey('');
                setAsaasWebhookToken('');
                setAsaasWebhookTouched(false);
                loadAsaas();
            })
            .catch((e) => setAsaasErr(e.message || String(e)))
            .finally(() => setAsaasBusy(false));
    };

    const inputCls = 'w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200';

    const tabs = [
        { id: 'asaas', label: tp('paymentGatewayTabAsaas') },
        { id: 'mercadopago', label: tp('paymentGatewayTabMercadoPago') },
        { id: 'stripe', label: tp('paymentGatewayTabStripe') },
    ];

    if (!authToken) {
        return <p className="text-sm text-slate-500 dark:text-slate-400">{tb('myApiNeedLogin')}</p>;
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">{tp('paymentGatewayTitle')}</h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{tp('paymentGatewaySubtitle')}</p>
            </div>

            <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-600 pb-px">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setProviderTab(tab.id)}
                        className={`rounded-t-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                            providerTab === tab.id
                                ? 'bg-white dark:bg-slate-800 text-[#0546ed] dark:text-blue-400 border border-b-0 border-slate-200 dark:border-slate-600 -mb-px'
                                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                {providerTab === 'asaas' && (
                    <div className="space-y-4">
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{tb('asaasIntegrationHint')}</p>
                        {asaasErr && (
                            <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                                {asaasErr}
                            </div>
                        )}
                        {moodleApi?.api_suspended && (
                            <div className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 rounded-lg px-3 py-2">{tb('myApiSuspended')}</div>
                        )}
                        {asaasLoading && !asaas ? (
                            <p className="text-sm text-slate-500">…</p>
                        ) : (
                            <>
                                {asaas?.has_key && (
                                    <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">{tb('asaasSaved')}</p>
                                )}
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{tb('asaasApiKeyLabel')}</label>
                                <input
                                    type="password"
                                    autoComplete="off"
                                    value={asaasKey}
                                    onChange={(e) => setAsaasKey(e.target.value)}
                                    placeholder={asaas?.has_key ? '•••• (deixe vazio para só alterar sandbox)' : ''}
                                    className={inputCls}
                                />
                                <label className="flex items-center gap-2 mt-3 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                                    <input type="checkbox" checked={asaasSandbox} onChange={(e) => setAsaasSandbox(e.target.checked)} />
                                    {tb('asaasSandboxLabel')}
                                </label>
                                <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-600">
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{tb('asaasWebhookTokenLabel')}</label>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 leading-relaxed">{tb('asaasWebhookTokenHint')}</p>
                                    {asaas?.has_webhook_token && (
                                        <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-2">{tb('asaasWebhookTokenSaved')}</p>
                                    )}
                                    <input
                                        type="password"
                                        autoComplete="off"
                                        value={asaasWebhookToken}
                                        onChange={(e) => { setAsaasWebhookToken(e.target.value); setAsaasWebhookTouched(true); }}
                                        placeholder={tb('asaasWebhookTokenPlaceholder')}
                                        className={inputCls}
                                    />
                                    <button
                                        type="button"
                                        className="mt-2 text-xs text-slate-600 dark:text-slate-400 underline"
                                        onClick={() => { setAsaasWebhookToken(''); setAsaasWebhookTouched(true); }}
                                    >
                                        {tb('asaasWebhookTokenClear')}
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    disabled={asaasBusy || moodleApi?.api_suspended}
                                    onClick={handleSaveAsaas}
                                    className="w-full mt-4 py-2.5 bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                                >
                                    {asaasBusy ? '…' : tb('asaasSaveIntegration')}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {providerTab === 'mercadopago' && (
                    <div className="space-y-4">
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{tb('mercadopagoIntegrationHint')}</p>
                        {mp?.tenant_id ? (
                            <p className="text-xs font-mono text-slate-700 dark:text-slate-200 break-all">
                                <span className="font-sans text-slate-500 dark:text-slate-400">{tb('mercadopagoTenantIdHint')} </span>
                                {String(mp.tenant_id)}
                            </p>
                        ) : null}
                        {mpErr && (
                            <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                                {mpErr}
                            </div>
                        )}
                        {moodleApi?.api_suspended && (
                            <div className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 rounded-lg px-3 py-2">{tb('myApiSuspended')}</div>
                        )}
                        {mpLoading && !mp ? (
                            <p className="text-sm text-slate-500">…</p>
                        ) : (
                            <>
                                {mp?.has_key && (
                                    <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">{tb('mercadopagoSaved')}</p>
                                )}
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{tb('mercadopagoAccessTokenLabel')}</label>
                                <input
                                    type="password"
                                    autoComplete="off"
                                    value={mpToken}
                                    onChange={(e) => setMpToken(e.target.value)}
                                    placeholder={mp?.has_key ? '•••• (deixe vazio para só alterar modo teste / webhook)' : ''}
                                    className={inputCls}
                                />
                                <label className="flex items-center gap-2 mt-3 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                                    <input type="checkbox" checked={mpTestMode} onChange={(e) => setMpTestMode(e.target.checked)} />
                                    {tb('mercadopagoTestModeLabel')}
                                </label>
                                <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-600">
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{tb('mercadopagoWebhookSecretLabel')}</label>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 leading-relaxed">{tb('mercadopagoWebhookSecretHint')}</p>
                                    {mp?.has_webhook_secret && (
                                        <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-2">{tb('mercadopagoWebhookSecretSaved')}</p>
                                    )}
                                    <input
                                        type="password"
                                        autoComplete="off"
                                        value={mpWebhookSecret}
                                        onChange={(e) => { setMpWebhookSecret(e.target.value); setMpWebhookTouched(true); }}
                                        placeholder={tb('mercadopagoWebhookSecretPlaceholder')}
                                        className={inputCls}
                                    />
                                    <button
                                        type="button"
                                        className="mt-2 text-xs text-slate-600 dark:text-slate-400 underline"
                                        onClick={() => { setMpWebhookSecret(''); setMpWebhookTouched(true); }}
                                    >
                                        {tb('mercadopagoWebhookSecretClear')}
                                    </button>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-mono break-all">
                                    {`${API_BASE.replace(/\/$/, '')}/api/webhooks/mercadopago?tid=${mp?.tenant_id || 'SEU_TENANT_ID'}`}
                                </p>
                                <button
                                    type="button"
                                    disabled={mpBusy || moodleApi?.api_suspended}
                                    onClick={handleSaveMp}
                                    className="w-full mt-4 py-2.5 bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                                >
                                    {mpBusy ? '…' : tb('mercadopagoSaveIntegration')}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {providerTab === 'stripe' && (
                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">{tp('paymentGatewayInDevelopment')}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
const UPLOADS_PAGE_SIZE = 20;
const UPLOAD_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const UPLOAD_FILE_TYPES = [
    { id: '', labelKey: 'mediaTypeAll' },
    { id: 'png', label: 'PNG' },
    { id: 'jpg', label: 'JPG' },
    { id: 'svg', label: 'SVG' },
    { id: 'gif', label: 'GIF' },
];

function uploadVisibleName(upload) {
    return String(upload?.display_name || upload?.filename || '').trim();
}

function uploadFirstLetter(upload) {
    const base = uploadVisibleName(upload).normalize('NFD').replace(/\p{M}/gu, '');
    const ch = base.charAt(0).toUpperCase();
    return /[A-Z]/.test(ch) ? ch : '';
}

function uploadKind(upload) {
    const blob = [upload?.filename, upload?.url_path, upload?.stored_name].join(' ').toLowerCase();
    const mime = String(upload?.mime_type || '').toLowerCase();
    if (blob.includes('.svg') || mime.includes('svg')) return 'svg';
    if (blob.includes('.gif') || mime.includes('gif')) return 'gif';
    if (blob.includes('.png') || mime.includes('png')) return 'png';
    if (/\.jpe?g(?:\b|$)/.test(blob) || mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
    return '';
}

function matchesUploadFilters(upload, letter, fileType) {
    if (letter && uploadFirstLetter(upload) !== letter) return false;
    if (fileType && uploadKind(upload) !== fileType) return false;
    return true;
}

export function UploadsPanel({ authToken, onCopyUrl, uploadsLocked = false, onOpenPlans, canCurateAiLibrary = false }) {
    const { t } = useTranslation('builder');
    const [uploads, setUploads] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [dragOver, setDragOver] = React.useState(false);
    const [drawerSearch, setDrawerSearch] = React.useState('');
    const [debouncedDrawerSearch, setDebouncedDrawerSearch] = React.useState('');
    const [galleryOpen, setGalleryOpen] = React.useState(false);
    const [copiedUploadId, setCopiedUploadId] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [total, setTotal] = React.useState(0);
    const [letter, setLetter] = React.useState('');
    const [fileType, setFileType] = React.useState('');
    const [reloadKey, setReloadKey] = React.useState(0);
    const fileInputRef = React.useRef(null);

    React.useEffect(() => {
        const tm = setTimeout(() => {
            const next = drawerSearch.trim();
            setDebouncedDrawerSearch((prev) => {
                if (prev !== next) setPage(0);
                return next;
            });
        }, 320);
        return () => clearTimeout(tm);
    }, [drawerSearch]);

    const loadUploads = React.useCallback(() => {
        if (!authToken) return;
        setLoading(true);
        setError('');
        const filtering = !!(letter || fileType);
        const headers = { Authorization: `Bearer ${authToken}` };
        const showRows = (rows, serverTotal) => {
            const list = filtering ? rows.filter((u) => matchesUploadFilters(u, letter, fileType)) : rows;
            if (filtering) {
                const maxPage = Math.max(0, Math.ceil(list.length / UPLOADS_PAGE_SIZE) - 1);
                if (page > maxPage) {
                    setPage(maxPage);
                    return;
                }
                const start = page * UPLOADS_PAGE_SIZE;
                setUploads(list.slice(start, start + UPLOADS_PAGE_SIZE));
                setTotal(list.length);
                return;
            }
            const tot = Number(serverTotal) || list.length;
            const maxPage = Math.max(0, Math.ceil(tot / UPLOADS_PAGE_SIZE) - 1);
            if (page > maxPage) {
                setPage(maxPage);
                return;
            }
            setUploads(list);
            setTotal(tot);
        };
        const qs = new URLSearchParams({
            limit: String(filtering ? 200 : UPLOADS_PAGE_SIZE),
            offset: String(filtering ? 0 : page * UPLOADS_PAGE_SIZE),
        });
        if (debouncedDrawerSearch) qs.set('q', debouncedDrawerSearch);
        if (letter) qs.set('letter', letter);
        if (fileType) qs.set('ext', fileType);
        const pull = (params) => fetch(`${API}/media?${params}`, { headers, cache: 'no-store' })
            .then(async (r) => {
                const data = await r.json().catch(() => ({}));
                if (!r.ok) throw new Error(data.error || 'load');
                return data;
            });
        pull(qs)
            .then((d) => showRows(d.uploads || [], d.total))
            .catch(() => {
                if (!filtering) {
                    setError('uploadErrorLoad');
                    setUploads([]);
                    setTotal(0);
                    return null;
                }
                const fallback = new URLSearchParams({ limit: '200', offset: '0' });
                if (debouncedDrawerSearch) fallback.set('q', debouncedDrawerSearch);
                return pull(fallback)
                    .then((d) => showRows(d.uploads || [], d.total))
                    .catch(() => { setError('uploadErrorLoad'); setUploads([]); setTotal(0); });
            })
            .finally(() => setLoading(false));
    }, [authToken, debouncedDrawerSearch, page, letter, fileType, reloadKey]);

    React.useEffect(() => loadUploads(), [loadUploads]);

    const handleFiles = (files) => {
        if (uploadsLocked || !files?.length || !authToken) return;
        const valid = Array.from(files).filter(f => ACCEPTED_TYPES.includes(f.type));
        if (valid.length === 0) {
            setError('uploadErrorTypes');
            return;
        }
        setUploading(true);
        setError('');
        let done = 0;
        valid.forEach((file) => {
            const fd = new FormData();
            fd.append('file', file);
            fetch(`${API}/media`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${authToken}` },
                body: fd
            })
                .then(r => r.json())
                .then(d => {
                    if (d.error) setError(d.error);
                })
                .catch(() => setError('uploadErrorSend'))
                .finally(() => {
                    done++;
                    if (done === valid.length) {
                        setUploading(false);
                        setPage(0);
                        setReloadKey((n) => n + 1);
                    }
                });
        });
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer?.files);
    };

    const copyUrl = (url, uploadId) => {
        navigator.clipboard?.writeText(url).then(() => {
            if (uploadId != null) {
                setCopiedUploadId(uploadId);
                window.setTimeout(() => setCopiedUploadId((x) => (x === uploadId ? null : x)), 2000);
            }
            if (onCopyUrl) onCopyUrl(url);
        });
    };

    const deleteUpload = (id) => {
        if (!authToken || !confirm(t('deleteConfirm'))) return;
        fetch(`${API}/media/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } })
            .then(r => r.json())
            .then(d => { if (d.success) loadUploads(); })
            .catch(() => {});
    };

    if (!authToken) {
        return (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-4">
                {t('uploadLoginRequired')}
            </p>
        );
    }

    return (
        <div className="space-y-6">
            <MediaGalleryModal
                open={galleryOpen}
                onClose={() => setGalleryOpen(false)}
                authToken={authToken}
                uploadsLocked={uploadsLocked}
                canCurateAiLibrary={canCurateAiLibrary}
            />
            {uploadsLocked && !canCurateAiLibrary ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
                    <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-200 mb-3">
                        {t('moduleUploadLockedHint')}
                    </p>
                    {typeof onOpenPlans === 'function' ? (
                        <button
                            type="button"
                            onClick={onOpenPlans}
                            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                        >
                            {t('upgradeNow')}
                        </button>
                    ) : null}
                </div>
            ) : null}
            <div
                className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-colors
                    ${uploadsLocked ? 'opacity-45 cursor-not-allowed pointer-events-none border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/40' : `cursor-pointer ${dragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-blue-400'}`}`}
                onClick={() => { if (!uploadsLocked) fileInputRef.current?.click(); }}
                onDragOver={(e) => { if (uploadsLocked) return; e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    multiple
                    className="hidden"
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                />
                {uploading ? (
                    <span className="text-blue-600 dark:text-blue-400 animate-pulse">{t('uploading')}</span>
                ) : (
                    <>
                        <UploadCloud size={32} className="text-blue-500 mb-3" />
                        <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-1">{t('uploadTitle')}</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {t('uploadHint')}
                        </p>
                    </>
                )}
            </div>

            {error && <p className="text-sm text-red-500 dark:text-red-400">{t(error)}</p>}

            <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <h4 className="font-medium text-slate-800 dark:text-slate-200 text-sm">{t('recentUploads')}</h4>
                    <button
                        type="button"
                        disabled={uploadsLocked && !canCurateAiLibrary}
                        onClick={() => { if (!uploadsLocked || canCurateAiLibrary) setGalleryOpen(true); }}
                        className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                    >
                        {canCurateAiLibrary && uploadsLocked
                            ? t('openAiMediaGallery', { defaultValue: 'Abrir biblioteca IA' })
                            : t('openMediaGallery', { defaultValue: 'Abrir galeria' })}
                    </button>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2 leading-snug">
                    {t('mediaDrawerDragHint', { defaultValue: 'Arraste uma miniatura até um bloco Imagem no palco para aplicar a URL.' })}
                </p>
                <div className="relative mb-3">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="search"
                        value={drawerSearch}
                        onChange={(e) => setDrawerSearch(e.target.value)}
                        placeholder={t('mediaSearchPlaceholder', { defaultValue: 'Buscar por nome ou tags…' })}
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                    />
                </div>
                <div className="mb-2" role="group" aria-label={t('mediaLetterFilter')}>
                    <div className="flex flex-wrap items-center">
                        {UPLOAD_LETTERS.map((ch, i) => (
                            <React.Fragment key={ch}>
                                {i > 0 ? <span className="text-slate-300 dark:text-slate-600 px-0.5 select-none text-[11px]" aria-hidden="true">|</span> : null}
                                <button
                                    type="button"
                                    aria-pressed={letter === ch}
                                    onClick={() => { setLetter((prev) => (prev === ch ? '' : ch)); setPage(0); }}
                                    className={`px-0.5 text-[11px] font-semibold bg-transparent border-0 cursor-pointer ${letter === ch ? 'text-blue-600 dark:text-blue-400 underline' : 'text-slate-500 dark:text-slate-400 hover:text-blue-600'}`}
                                >
                                    {ch}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-3" role="group" aria-label={t('mediaTypeFilter')}>
                    {UPLOAD_FILE_TYPES.map((item) => (
                        <button
                            key={item.id || 'all'}
                            type="button"
                            aria-pressed={fileType === item.id}
                            onClick={() => { setFileType(item.id); setPage(0); }}
                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border cursor-pointer ${fileType === item.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}
                        >
                            {item.labelKey ? t(item.labelKey) : item.label}
                        </button>
                    ))}
                </div>
                {loading ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('loading')}</p>
                ) : uploads.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 py-4">{(letter || fileType || debouncedDrawerSearch) ? t('mediaFilterEmpty') : t('noUploads')}</p>
                ) : (
                    <div className="grid grid-cols-3 gap-2">
                        {uploads.map((u) => (
                            <div
                                key={u.id}
                                className="aspect-square bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden relative group cursor-grab active:cursor-grabbing select-none"
                                draggable
                                title={t('mediaDragToImage', { defaultValue: 'Arraste até o elemento Imagem no palco' })}
                                onDragStart={(ev) => {
                                    if (ev.target?.closest?.('button')) {
                                        ev.preventDefault();
                                        return;
                                    }
                                    setGepettoMediaDragData(ev.dataTransfer, u.url);
                                }}
                            >
                                <img
                                    src={u.url}
                                    alt={u.display_name || u.filename}
                                    className="w-full h-full object-cover pointer-events-none"
                                    draggable={false}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                {copiedUploadId === u.id && (
                                    <span className="absolute top-1 left-1 z-20 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-600 text-white shadow pointer-events-none">
                                        {t('urlCopied', { defaultValue: 'Copiado!' })}
                                    </span>
                                )}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
                                    <button
                                        type="button"
                                        onClick={(ev) => { ev.stopPropagation(); copyUrl(u.url, u.id); }}
                                        className="p-2 bg-white text-slate-800 rounded-md hover:bg-blue-50 hover:text-blue-600 pointer-events-auto"
                                        title={t('copyUrl')}
                                    >
                                        <Copy size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(ev) => { ev.stopPropagation(); deleteUpload(u.id); }}
                                        className="p-2 bg-white text-slate-800 rounded-md hover:bg-red-50 hover:text-red-600 pointer-events-auto"
                                        title={t('delete')}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                                <span className="absolute bottom-0 left-0 right-0 text-[10px] text-white bg-black/60 px-1 truncate" title={u.display_name || u.filename}>
                                    {u.display_name || u.filename}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {total > UPLOADS_PAGE_SIZE && (
                    <div className="flex items-center justify-between gap-2 mt-3 text-xs text-slate-600 dark:text-slate-300">
                        <button
                            type="button"
                            disabled={page <= 0 || loading}
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            className="px-2 py-1 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                        >
                            {t('mediaPagePrev')}
                        </button>
                        <span className="tabular-nums text-center">
                            {t('mediaPageStatus', {
                                page: page + 1,
                                pages: Math.max(1, Math.ceil(total / UPLOADS_PAGE_SIZE)),
                                from: page * UPLOADS_PAGE_SIZE + 1,
                                to: Math.min(total, (page + 1) * UPLOADS_PAGE_SIZE),
                                total,
                            })}
                        </span>
                        <button
                            type="button"
                            disabled={loading || (page + 1) * UPLOADS_PAGE_SIZE >= total}
                            onClick={() => setPage((p) => p + 1)}
                            className="px-2 py-1 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                        >
                            {t('mediaPageNext')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/** Constrói árvore de projetos a partir da lista plana */
function buildProjectTree(projects) {
    const byId = {};
    const roots = [];
    (projects || []).forEach(p => { byId[p.id] = { ...p, children: [] }; });
    (projects || []).forEach(p => {
        const node = byId[p.id];
        if (!p.parent_id) roots.push(node);
        else if (byId[p.parent_id]) byId[p.parent_id].children.push(node);
        else roots.push(node);
    });
    const sortFn = (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.course_name || '').localeCompare(b.course_name || '');
    roots.sort(sortFn);
    const sortChildren = (nodes) => {
        nodes.sort(sortFn);
        nodes.forEach(n => n.children?.length && sortChildren(n.children));
    };
    sortChildren(roots);
    return roots;
}

/** Lista plana de pastas para dropdown de mover */
function getFoldersFlat(projects, excludeId = null) {
    return (projects || []).filter(p => p.type === 'folder' && p.id !== excludeId);
}

/** Componente de árvore reutilizável para Modal Ver Projetos */
export function ProjectsTreeModal({ projectsList = [], expandedIds, onToggleExpand, onOpenProject, onRenameProject, onReuseProject, onDeleteProject, onMoveProject, onClose, t, locale }) {
    const [moveTarget, setMoveTarget] = useState(null);
    const tree = buildProjectTree(projectsList);

    const renderItem = (node, depth = 0) => {
        const isFolder = node.type === 'folder';
        const isExpanded = expandedIds.has(node.id);
        const indent = depth * 20;

        return (
            <div key={node.id} className="space-y-1">
                <div
                    className={`rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-all group ${isFolder ? 'cursor-pointer' : ''}`}
                    style={{ marginLeft: indent }}
                >
                    {/* Linha 1: ícone + nome (preenche toda a linha) */}
                    <div
                        className="flex items-center gap-2 p-4"
                        onClick={() => isFolder ? onToggleExpand(node.id) : onOpenProject(node)}
                    >
                        <button onClick={(e) => { e.stopPropagation(); if (isFolder) onToggleExpand(node.id); }} className="p-0.5 text-slate-400 shrink-0">
                            {isFolder ? (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <span style={{ width: 20, display: 'inline-block' }} />}
                        </button>
                        {isFolder ? <Folder size={18} className="text-amber-500 shrink-0" /> : <LayoutTemplate size={18} className="text-blue-500 shrink-0" />}
                        <h4 className="font-medium text-slate-800 dark:text-slate-200 flex-1 min-w-0 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" title={node.course_name || t('builder:untitled')}>
                            {node.course_name || t('builder:untitled')}
                        </h4>
                    </div>
                    {/* Linha 2: data + ícones de ação */}
                    <div className="flex items-center justify-between px-4 pb-4 pt-0 gap-2">
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                            {!isFolder && node.updated_at ? new Date(node.updated_at).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '\u00A0'}
                        </span>
                        <div className="flex items-center gap-2">
                            {moveTarget === node.id ? (
                                <select
                                    className="text-xs border rounded px-2 py-1 bg-white dark:bg-slate-600"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val && onMoveProject) onMoveProject(node.id, val === '__root__' ? null : val);
                                        setMoveTarget(null);
                                    }}
                                    onBlur={() => setMoveTarget(null)}
                                    onClick={(e) => e.stopPropagation()}
                                    autoFocus
                                >
                                    <option value="">{t('builder:moveTo', { defaultValue: 'Mover...' })}</option>
                                    <option value="__root__">{t('builder:rootFolder', { defaultValue: 'Raiz' })}</option>
                                    {getFoldersFlat(projectsList, node.id).map(f => (
                                        <option key={f.id} value={f.id}>{f.course_name || t('builder:untitled')}</option>
                                    ))}
                                </select>
                            ) : (
                                <>
                                    {!isFolder && <button onClick={(e) => { e.stopPropagation(); onOpenProject(node); }} className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-100 dark:hover:bg-blue-900/60 rounded-lg">{t('builder:open')}</button>}
                                    <button onClick={(e) => { e.stopPropagation(); onRenameProject(node); }} className="px-2 py-1.5 text-xs text-slate-500" title={t('builder:renameProject')}><Pencil size={14} /></button>
                                    {!isFolder && <button onClick={(e) => { e.stopPropagation(); onReuseProject(node); }} className="px-2 py-1.5 text-xs text-emerald-500" title={t('builder:reuseProject')}><CopyPlus size={14} /></button>}
                                    <button onClick={(e) => { e.stopPropagation(); setMoveTarget(node.id); }} className="px-2 py-1.5 text-xs text-slate-500" title={t('builder:moveProject', { defaultValue: 'Mover' })}><GripVertical size={14} /></button>
                                    <button onClick={(e) => { e.stopPropagation(); onDeleteProject(node.id); }} className="px-2 py-1.5 text-xs text-red-400 hover:text-red-600" title={t('builder:delete')}><Trash2 size={14} /></button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {isFolder && isExpanded && node.children?.map(child => renderItem(child, depth + 1))}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
            {tree.length === 0 ? (
                <div className="text-center py-10 text-slate-400 dark:text-slate-500">
                    <FolderOpen size={40} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                    <p>{t('builder:noProjects')}</p>
                </div>
            ) : (
                tree.map(node => renderItem(node))
            )}
        </div>
    );
}

export function ProjetosPanel({ projectsList = [], onOpenProject, onDeleteProject, onNewProject, onNewFolder, onRenameProject, onReuseProject, onMoveProject }) {
    const { t, i18n } = useTranslation('builder');
    const locale = i18n.language === 'pt' ? 'pt-BR' : 'en-US';
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [moveTarget, setMoveTarget] = useState(null);

    const toggleExpand = (id) => setExpandedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const tree = buildProjectTree(projectsList);

    const [dragOverId, setDragOverId] = useState(null);
    const [draggedId, setDraggedId] = useState(null);

    const handleDragStart = (e, node) => {
        if (node.type === 'folder') return;
        setDraggedId(node.id);
        e.dataTransfer.setData('text/plain', node.id);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e, targetId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverId(targetId);
    };
    const handleDragLeave = () => setDragOverId(null);
    const handleDrop = (e, targetFolderId) => {
        e.preventDefault();
        setDragOverId(null);
        setDraggedId(null);
        const id = e.dataTransfer.getData('text/plain');
        if (!id || !onMoveProject) return;
        onMoveProject(id, targetFolderId);
    };
    const handleDragEnd = () => {
        setDraggedId(null);
        setDragOverId(null);
    };

    const renderItem = (node, depth = 0) => {
        const isFolder = node.type === 'folder';
        const isExpanded = expandedIds.has(node.id);
        const indent = depth * 20;
        const isDragging = draggedId === node.id;
        const isDropTarget = dragOverId === node.id;
        const canDrop = draggedId && draggedId !== node.id && (isFolder || true);
        const isLocalOnly = !!(node.local_only || node.localOnly);

        return (
            <div key={node.id} className="space-y-1">
                <div
                    className={`rounded-lg border transition-all group ${isFolder ? 'cursor-pointer' : ''} ${
                        isDragging ? 'opacity-50' : ''
                    } ${isDropTarget && canDrop ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/30 border-blue-400' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/80 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                    style={{ marginLeft: indent }}
                    draggable={!isFolder && !isLocalOnly}
                    onDragStart={(e) => handleDragStart(e, node)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => canDrop && handleDragOver(e, node.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => canDrop && handleDrop(e, isFolder ? node.id : (node.parent_id || null))}
                >
                    {/* Linha 1: ícone + nome (preenche toda a linha) */}
                    <div
                        className="flex items-center gap-2 p-2.5"
                        onClick={() => isFolder ? toggleExpand(node.id) : (onOpenProject && onOpenProject(node))}
                    >
                        <button
                            onClick={(e) => { e.stopPropagation(); if (isFolder) toggleExpand(node.id); }}
                            className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 shrink-0"
                        >
                            {isFolder ? (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <span style={{ width: 20, display: 'inline-block' }} />}
                        </button>
                        {isFolder ? <Folder size={18} className="text-amber-500 shrink-0" /> : <LayoutTemplate size={18} className="text-blue-500 shrink-0" />}
                        <h4 className="font-medium text-slate-800 dark:text-slate-200 text-sm flex-1 min-w-0 truncate flex items-center gap-1.5 min-w-0" title={node.course_name || t('untitled')}>
                            <span className="truncate">{node.course_name || t('untitled')}</span>
                            {isLocalOnly && (
                                <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800">
                                    {t('builder:freeLocalBadge')}
                                </span>
                            )}
                        </h4>
                    </div>
                    {/* Linha 2: data + ícones de ação */}
                    <div className="flex items-center justify-between px-2.5 pb-2 pt-0 gap-2">
                        <span className="text-[11px] text-slate-400 dark:text-slate-500">
                            {!isFolder && (isLocalOnly && node.expires_at
                                ? t('builder:freeLocalExpiresHint', {
                                    time: new Date(node.expires_at).toLocaleString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
                                })
                                : node.updated_at
                                    ? new Date(node.updated_at).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    : '\u00A0')}
                        </span>
                        <div className="flex items-center gap-0.5">
                            {moveTarget === node.id ? (
                                <select
                                    className="text-xs border rounded px-1.5 py-0.5 bg-white dark:bg-slate-600"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val && onMoveProject) onMoveProject(node.id, val === '__root__' ? null : val);
                                        setMoveTarget(null);
                                    }}
                                    onBlur={() => setMoveTarget(null)}
                                    onClick={(e) => e.stopPropagation()}
                                    autoFocus
                                >
                                    <option value="">{t('builder:moveTo', { defaultValue: 'Mover para...' })}</option>
                                    <option value="__root__">{t('builder:rootFolder', { defaultValue: 'Raiz' })}</option>
                                    {getFoldersFlat(projectsList, node.id).map(f => (
                                        <option key={f.id} value={f.id}>{f.course_name || t('untitled')}</option>
                                    ))}
                                </select>
                            ) : (
                                <>
                                    <button onClick={(e) => { e.stopPropagation(); onRenameProject && onRenameProject(node); }} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600" title={t('renameProject')}><Pencil size={12} /></button>
                                    {!isFolder && <button onClick={(e) => { e.stopPropagation(); onReuseProject && onReuseProject(node); }} className="p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-emerald-600" title={t('reuseProject')}><CopyPlus size={12} /></button>}
                                    <button onClick={(e) => { e.stopPropagation(); setMoveTarget(node.id); }} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-blue-600 dark:text-blue-400" title={t('builder:moveProject', { defaultValue: 'Mover para pasta' })}><FolderInput size={12} /></button>
                                    <button onClick={(e) => { e.stopPropagation(); onDeleteProject && onDeleteProject(node.id); }} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/40 text-red-500" title={t('delete')}><Trash2 size={12} /></button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {isFolder && isExpanded && node.children?.map(child => renderItem(child, depth + 1))}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex gap-2">
                <button
                    onClick={() => onNewProject && onNewProject(null)}
                    className="flex-1 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800 rounded-lg text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center justify-center gap-1.5"
                >
                    <Plus size={14} />
                    {t('newProject')}
                </button>
                <button
                    onClick={() => onNewFolder && onNewFolder(null)}
                    className="flex-1 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-lg text-sm font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors flex items-center justify-center gap-1.5"
                    title={t('builder:newFolder', { defaultValue: 'Nova pasta' })}
                >
                    <Folder size={14} />
                    {t('builder:newFolder', { defaultValue: 'Nova pasta' })}
                </button>
            </div>

            <div className="space-y-1 mt-4 max-h-[50vh] overflow-y-auto">
                {tree.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                        <FolderOpen size={32} className="mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">{t('noProjects')}</p>
                    </div>
                ) : (
                    <>
                        {draggedId && getFoldersFlat(projectsList).length > 0 && (
                            <div
                                className={`rounded-lg border-2 border-dashed p-2 text-center text-xs transition-colors ${
                                    dragOverId === '__root__'
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                        : 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50'
                                }`}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId('__root__'); }}
                                onDragLeave={() => setDragOverId(null)}
                                onDrop={(e) => { e.preventDefault(); setDragOverId(null); setDraggedId(null); const id = e.dataTransfer.getData('text/plain'); if (id && onMoveProject) onMoveProject(id, null); }}
                            >
                                {t('builder:dropHereForRoot', { defaultValue: 'Solte aqui para mover para Raiz' })}
                            </div>
                        )}
                        {tree.map(node => renderItem(node))}
                    </>
                )}
            </div>
        </div>
    );
}

/** Painel «Mídias globais» — fora do menu por ora; reativar em App.jsx (import + SidebarItem + activePanel). */
export function MidiasPanel({ onCopyUrl }) {
    const { t } = useTranslation('builder');
    const [copiedMediaId, setCopiedMediaId] = React.useState(null);
    const [pastaAtiva, setPastaAtiva] = React.useState(0);
    const pastas = [t('mediaFolderGeneral'), t('mediaFolderCourses'), t('mediaFolderBanners'), t('mediaFolderLogos')];
    // Mídias globais (futuro: inseridas pelo admin, criadas por IA)
    const [midias] = React.useState([
        { id: 1, url: "https://picsum.photos/seed/midia1/200/200", nome: "imagem-1.jpg" },
        { id: 2, url: "https://picsum.photos/seed/midia2/200/200", nome: "imagem-2.jpg" },
        { id: 3, url: "https://picsum.photos/seed/midia3/200/200", nome: "imagem-3.jpg" },
        { id: 4, url: "https://picsum.photos/seed/midia4/200/200", nome: "imagem-4.jpg" },
        { id: 5, url: "https://picsum.photos/seed/midia5/200/200", nome: "imagem-5.jpg" },
        { id: 6, url: "https://picsum.photos/seed/midia6/200/200", nome: "imagem-6.jpg" },
    ]);

    const copyUrl = (url, mediaId) => {
        navigator.clipboard?.writeText(url).then(() => {
            if (mediaId != null) {
                setCopiedMediaId(mediaId);
                window.setTimeout(() => setCopiedMediaId((x) => (x === mediaId ? null : x)), 2000);
            }
            if (onCopyUrl) onCopyUrl(url);
        });
    };

    return (
        <div className="space-y-6">
            <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('mediaGlobalHint')}
            </p>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {pastas.map((pasta, i) => (
                    <button
                        key={i}
                        onClick={() => setPastaAtiva(i)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${i === pastaAtiva ? "bg-slate-800 text-white dark:bg-slate-600" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"}`}
                    >
                        {pasta}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
                {midias.map((m) => (
                    <div
                        key={m.id}
                        className="aspect-square bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden relative group border border-slate-200 dark:border-slate-600 cursor-grab active:cursor-grabbing select-none"
                        draggable
                        title={t('mediaDragToImage', { defaultValue: 'Arraste até o elemento Imagem no palco' })}
                        onDragStart={(ev) => {
                            if (ev.target?.closest?.('button')) {
                                ev.preventDefault();
                                return;
                            }
                            setGepettoMediaDragData(ev.dataTransfer, m.url);
                        }}
                    >
                        <img
                            src={m.url}
                            alt={m.nome}
                            className="w-full h-full object-cover pointer-events-none"
                            draggable={false}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {copiedMediaId === m.id && (
                            <span className="absolute top-1 left-1 z-20 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-600 text-white shadow pointer-events-none">
                                {t('urlCopied', { defaultValue: 'Copiado!' })}
                            </span>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
                            <button
                                type="button"
                                onClick={(ev) => { ev.stopPropagation(); copyUrl(m.url, m.id); }}
                                className="p-2 bg-white text-slate-800 rounded-md hover:bg-blue-50 hover:text-blue-600 pointer-events-auto"
                                title={t('copyUrl')}
                            >
                                <Copy size={16} />
                            </button>
                        </div>
                        <span className="absolute bottom-0 left-0 right-0 text-[10px] text-white bg-black/60 px-1 truncate" title={m.nome}>
                            {m.nome}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Ícone colorido por tipo de ação (mock Stitch / logs). */
function ActivityTypeIcon({ summary, action }) {
    const s = `${summary || ''} ${action || ''}`.toLowerCase();
    if (s.includes('exclu') || s.includes('delet') || s.includes('remov') || s.includes('archiv')) {
        return (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#b41340]/10 text-[#b41340]">
                <Trash2 size={20} strokeWidth={2.2} />
            </div>
        );
    }
    if (s.includes('convid') || s.includes('novo') || s.includes('invite') || s.includes('add')) {
        return (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#0546ed]/10 text-[#0546ed]">
                <PlusCircle size={20} strokeWidth={2.2} />
            </div>
        );
    }
    if (s.includes('edit') || s.includes('alter') || s.includes('accord')) {
        return (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#006947]/10 text-[#006947]">
                <Pencil size={20} strokeWidth={2.2} />
            </div>
        );
    }
    return (
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#0546ed]/10 text-[#0546ed]">
            <Save size={20} strokeWidth={2.2} />
        </div>
    );
}

function formatRelativeShort(iso, lang) {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (lang === 'pt' || String(lang).startsWith('pt')) {
        if (mins < 1) return 'Há instantes';
        if (mins < 60) return `Há ${mins} minutos`;
        if (hrs < 24) return `Há ${hrs} horas`;
        if (days === 1) return 'Há 1 dia';
        return `Há ${days} dias`;
    }
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    if (hrs < 24) return `${hrs} h ago`;
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
}

export function IndiquePanel({ user }) {
    const { t } = useTranslation(['profile']);
    const [copied, setCopied] = useState(false);
    const slug = (user?.email || 'convidado').split('@')[0].replace(/[^a-z0-9_-]/gi, '') || 'convidado';
    const inviteUrl = `atmp.re/${slug}-7291`;

    const copyLink = useCallback(() => {
        const full = `https://${inviteUrl}`;
        navigator.clipboard?.writeText(full).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
    }, [inviteUrl]);

    const fh = ATM_FH;

    return (
        <div className={`space-y-10 text-[#2c2f30] dark:text-slate-100 ${fh}`}>
            <div className="mb-16 flex flex-col justify-between gap-8 md:flex-row md:items-start">
                <div className="max-w-xl">
                    <h2 className="mb-6 text-4xl font-extrabold leading-tight tracking-tight text-[#2c2f30] dark:text-slate-50 md:text-5xl">
                        {t('profile:indiqueHeadlineBefore')}{' '}
                        <span className="text-[#0546ed]">{t('profile:indiqueHeadlineAccent')}</span>
                    </h2>
                    <p className="text-lg leading-relaxed text-[#595c5d] dark:text-slate-400">
                        {t('profile:indiqueSub')}
                    </p>
                </div>
                <div className="flex min-w-[220px] flex-col items-center rounded-2xl border border-[#0546ed]/10 bg-[#849aff]/20 p-8 text-center dark:border-blue-500/20 dark:bg-blue-950/30">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0546ed] shadow-lg shadow-[#0546ed]/25">
                        <Gift size={32} className="text-white" strokeWidth={1.75} />
                    </div>
                    <p className="text-2xl font-bold text-[#0546ed]">{t('profile:indiqueBonusValue')}</p>
                    <p className="text-sm font-semibold text-[#595c5d] dark:text-slate-400">{t('profile:indiqueBonusLabel')}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="relative overflow-hidden rounded-xl border border-[#abadae]/15 bg-white p-8 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:col-span-2">
                    <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#0546ed]/5 blur-3xl transition-all group-hover:bg-[#0546ed]/10" />
                    <div className="relative z-10">
                        <div className="mb-8 flex items-center gap-2">
                            <span className="rounded-full bg-[#69f6b8] px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#005a3c]">
                                {t('profile:indiqueStatusPill')}
                            </span>
                        </div>
                        <div className="flex flex-col items-center py-8 text-center">
                            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#eff1f2] dark:bg-slate-700">
                                <Construction className="h-10 w-10 text-[#d1d5d7] dark:text-slate-500" strokeWidth={1.5} />
                            </div>
                            <h3 className="mb-3 text-2xl font-bold text-[#2c2f30] dark:text-slate-100">{t('profile:indiqueBuildingTitle')}</h3>
                            <p className="mx-auto max-w-sm text-sm text-[#595c5d] dark:text-slate-400">{t('profile:indiqueBuildingDesc')}</p>
                            <div className="mt-10 h-2 w-full max-w-md overflow-hidden rounded-full bg-[#eff1f2] dark:bg-slate-700">
                                <div className="h-full w-[85%] rounded-full bg-[#0546ed] shadow-[0_0_12px_rgba(5,70,237,0.4)]" />
                            </div>
                            <div className="mt-3 flex w-full max-w-md justify-between text-[10px] font-bold uppercase tracking-tighter text-[#595c5d]/60 dark:text-slate-500">
                                <span>{t('profile:indiquePhaseDesign')}</span>
                                <span>{t('profile:indiquePhaseDev')}</span>
                                <span>{t('profile:indiquePhaseLaunch')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col justify-between rounded-xl bg-[#e0e3e4] p-8 dark:bg-slate-800/90">
                    <div>
                        <h4 className="mb-4 text-lg font-bold text-[#2c2f30] dark:text-slate-100">{t('profile:indiqueLinkTitle')}</h4>
                        <p className="mb-6 text-sm text-[#595c5d] dark:text-slate-400">{t('profile:indiqueLinkHint')}</p>
                        <div className="rounded-xl border border-[#abadae]/20 bg-white p-4 dark:border-slate-600 dark:bg-slate-900">
                            <p className="mb-2 text-[10px] font-bold uppercase text-[#595c5d]">{t('profile:indiqueInviteLinkLabel')}</p>
                            <div className="flex items-center justify-between gap-2">
                                <span className="truncate text-sm font-medium text-[#0546ed]">{inviteUrl}</span>
                                <button type="button" onClick={copyLink} className="rounded-lg p-2 text-[#595c5d] transition-colors hover:bg-[#eff1f2] dark:hover:bg-slate-800" title={t('profile:copyHint')}>
                                    <Copy size={18} />
                                </button>
                            </div>
                            {copied ? <p className="mt-1 text-xs text-[#006947]">{t('profile:copyHint')}</p> : null}
                        </div>
                    </div>
                    <div className="mt-8 border-t border-[#abadae]/20 pt-8 dark:border-slate-700">
                        <p className="mb-4 text-[10px] font-bold uppercase text-[#595c5d]">{t('profile:indiqueShareQuick')}</p>
                        <div className="flex gap-3">
                            <button type="button" className="flex flex-1 items-center justify-center rounded-xl border border-[#abadae]/10 bg-white py-3 transition-all hover:shadow-md dark:border-slate-600 dark:bg-slate-900">
                                <MessageCircle className="text-xl text-[#25D366]" size={22} />
                            </button>
                            <button type="button" className="flex flex-1 items-center justify-center rounded-xl border border-[#abadae]/10 bg-white py-3 transition-all hover:shadow-md dark:border-slate-600 dark:bg-slate-900">
                                <Share2 className="text-xl text-[#1877F2]" size={22} />
                            </button>
                            <button type="button" className="flex flex-1 items-center justify-center rounded-xl border border-[#abadae]/10 bg-white py-3 transition-all hover:shadow-md dark:border-slate-600 dark:bg-slate-900">
                                <Mail className="text-xl text-[#2c2f30] dark:text-slate-300" size={22} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
                {[
                    { k: 'indiqueStatViews', val: '124', trend: 'indiqueStatViewsTrend', Icon: TrendingUp },
                    { k: 'indiqueStatSignups', val: '12', trend: 'indiqueStatSignupsTrend', Icon: TrendingUp },
                    { k: 'indiqueStatConversion', val: '9.6%', trend: 'indiqueStatStable', Icon: Minus },
                    { k: 'credits', val: t('profile:indiqueMockAmount'), sub: 'indiqueStatCreditsSub', highlight: true },
                ].map((row) => (
                    <div
                        key={row.k}
                        className={`rounded-xl p-6 ${row.highlight ? 'border-2 border-[#0546ed]/20 bg-gradient-to-br from-[#eff1f2] to-[#0546ed]/5 dark:from-slate-800 dark:to-blue-950/40' : 'bg-[#eff1f2] dark:bg-slate-800/80'}`}
                    >
                        {row.highlight ? (
                            <>
                                <p className="mb-1 text-xs font-bold uppercase text-[#0546ed]">{t('profile:indiqueStatCredits')}</p>
                                <p className="text-3xl font-black text-[#0546ed]">{row.val}</p>
                                <p className="mt-2 text-xs font-medium text-[#595c5d] dark:text-slate-400">{t(row.sub)}</p>
                            </>
                        ) : (
                            <>
                                <p className="mb-1 text-xs font-bold uppercase text-[#595c5d] dark:text-slate-400">{t(`profile:${row.k}`)}</p>
                                <p className="text-3xl font-black text-[#2c2f30] dark:text-slate-100">{row.val}</p>
                                <div className={`mt-2 flex items-center text-xs font-bold ${row.k === 'indiqueStatConversion' ? 'text-[#595c5d]/60 dark:text-slate-500' : 'text-[#006947]'}`}>
                                    {row.Icon ? <row.Icon size={14} className="mr-1" /> : null}
                                    {t(`profile:${row.trend}`)}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            <div className="rounded-2xl bg-gradient-to-r from-[#0546ed] via-[#849aff] to-[#006947] p-px">
                <div className="flex flex-col items-center justify-between gap-8 rounded-[14px] bg-white/80 px-8 py-10 backdrop-blur-md dark:bg-slate-900/85 md:flex-row">
                    <div className="flex items-center gap-6">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/40 dark:bg-slate-800">
                            <Rocket className="text-3xl text-[#0546ed]" size={32} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-[#2c2f30] dark:text-slate-100">{t('profile:indiquePioneerTitle')}</h3>
                            <p className="text-[#595c5d] dark:text-slate-400">{t('profile:indiquePioneerDesc')}</p>
                        </div>
                    </div>
                    <button type="button" className="flex items-center gap-2 whitespace-nowrap rounded-xl bg-gradient-to-r from-[#0546ed] to-[#003cd3] px-8 py-4 text-sm font-bold text-white shadow-xl shadow-[#0546ed]/30 transition-transform hover:scale-[0.98]">
                        {t('profile:indiquePioneerCta')}
                        <ArrowRight size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Campo de seleção de projeto com busca por nome (ajax-style: digitar filtra a lista). */
function ProjectSearchSelect({ options = [], value, onChange, label, placeholder, allowEmpty, emptyLabel }) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const inputRef = React.useRef(null);
    const listRef = React.useRef(null);

    const displayName = options.find((o) => o.id === value)?.course_name || (allowEmpty && !value ? emptyLabel : '');
    const filtered = useMemo(() => {
        const q = (query || '').trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => (o.course_name || o.id || '').toLowerCase().includes(q));
    }, [options, query]);

    React.useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    React.useEffect(() => {
        const onOutside = (e) => {
            if (listRef.current && !listRef.current.contains(e.target) && inputRef.current && !inputRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, []);

    return (
        <div className="relative space-y-1.5">
            {label ? (typeof label === 'string' ? <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label> : <div>{label}</div>) : null}
            <div
                ref={inputRef}
                className="flex items-center gap-2 w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-sm cursor-text"
                onClick={() => { setOpen(true); inputRef.current?.querySelector('input')?.focus(); }}
            >
                <Search size={16} className="text-slate-400 flex-shrink-0" />
                <input
                    type="text"
                    value={open ? query : displayName}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    className="flex-1 min-w-0 bg-transparent border-0 outline-none"
                />
                <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </div>
            {open && (
                <div
                    ref={listRef}
                    className="absolute z-50 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1"
                >
                    {allowEmpty && (
                        <button
                            type="button"
                            onClick={() => { onChange(''); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 ${!value ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300'}`}
                        >
                            {emptyLabel}
                        </button>
                    )}
                    {filtered.slice(0, 50).map((o) => (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => { onChange(o.id); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-sm truncate hover:bg-slate-100 dark:hover:bg-slate-700 ${value === o.id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300'}`}
                        >
                            {o.course_name || o.id || '—'}
                        </button>
                    ))}
                    {filtered.length === 0 && <div className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400 text-center">Nenhum projeto encontrado</div>}
                </div>
            )}
        </div>
    );
}

/** Aba Perfil: últimas alterações em todos os projetos (até 1000 linhas no servidor). */
export function RecentActivityPanel({ authToken }) {
    const { t, i18n } = useTranslation(['profile', 'collab']);
    const [activity, setActivity] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [projectOpts, setProjectOpts] = React.useState([]);
    const [projectId, setProjectId] = React.useState('');
    const [ userSearch, setUserSearch ] = React.useState('');
    const [ elementSearch, setElementSearch ] = React.useState('');
    const [from, setFrom] = React.useState('');
    const [to, setTo] = React.useState('');
    const [page, setPage] = React.useState(1);
    const pageSize = 5;
    const fh = ATM_FH;

    const headers = () => ({
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
    });

    React.useEffect(() => {
        if (!authToken) return;
        fetch(`${API}/layouts`, { headers: headers() })
            .then((r) => (r.ok ? r.json() : { projects: [] }))
            .then((d) => {
                const projs = Array.isArray(d.projects) ? d.projects : [];
                const isNotFolder = (p) => p.type !== 'folder' && p.type !== 'Folder';
                setProjectOpts(projs.filter(isNotFolder));
            })
            .catch(() => setProjectOpts([]));
    }, [authToken]);

    React.useEffect(() => {
        if (!authToken) return;
        const h = setTimeout(() => {
            setLoading(true);
            const qs = new URLSearchParams();
            if (projectId) qs.set('projectId', projectId);
            if (userSearch.trim()) qs.set('userSearch', userSearch.trim());
            if (elementSearch.trim()) qs.set('elementSearch', elementSearch.trim());
            if (from) qs.set('from', from);
            if (to) qs.set('to', to);
            fetch(`${API}/activity-log?${qs.toString()}`, { headers: headers() })
                .then((r) => (r.ok ? r.json() : { activity: [] }))
                .then((d) => setActivity(Array.isArray(d.activity) ? d.activity : []))
                .catch(() => setActivity([]))
                .finally(() => setLoading(false));
        }, 320);
        return () => clearTimeout(h);
    }, [authToken, projectId, userSearch, elementSearch, from, to]);

    React.useEffect(() => {
        setPage(1);
    }, [projectId, userSearch, elementSearch, from, to]);

    const total = activity.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages);
    const slice = activity.slice((safePage - 1) * pageSize, safePage * pageSize);
    const topUser = useMemo(() => {
        const counts = {};
        activity.forEach((a) => {
            const n = a.user_name || a.user_id || '';
            if (!n) return;
            counts[n] = (counts[n] || 0) + 1;
        });
        let best = null;
        let max = 0;
        Object.entries(counts).forEach(([name, c]) => {
            if (c > max) { max = c; best = name; }
        });
        return best ? { name: best, count: max } : { name: '—', count: 0 };
    }, [activity]);

    const labelClass = 'text-xs font-bold uppercase tracking-wider text-[#595c5d] dark:text-slate-400 px-1';
    const inputRound = 'w-full rounded-xl border-none bg-white py-2.5 pl-4 pr-4 text-sm shadow-sm focus:ring-2 focus:ring-[#0546ed]/20 dark:bg-slate-900';

    return (
        <div className={`w-full space-y-8 text-[#2c2f30] dark:text-slate-100 ${fh}`}>
            <div className="flex flex-col gap-2">
                <h2 className="text-4xl font-extrabold tracking-tight text-[#2c2f30] dark:text-slate-50">{t('profile:lastChangesTitle')}</h2>
                <p className="font-medium text-[#595c5d] dark:text-slate-400">{t('profile:activitySubtitle')}</p>
                <p className="text-xs text-[#595c5d]/90 dark:text-slate-500">{t('profile:lastChangesLimitNotice')}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 rounded-2xl bg-[#eff1f2] p-4 md:grid-cols-4 dark:bg-slate-800/60">
                <ProjectSearchSelect
                    options={projectOpts}
                    value={projectId}
                    onChange={setProjectId}
                    label={<span className={labelClass}>{t('profile:activityFilterProject')}</span>}
                    placeholder={t('profile:activityFilterProjectPlaceholder')}
                    allowEmpty
                    emptyLabel={t('profile:activityAllProjects')}
                />
                <div className="space-y-1.5">
                    <label className={labelClass}>{t('profile:activityFilterUser')}</label>
                    <div className="relative">
                        <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder={t('profile:activityFilterUserPlaceholder')}
                            className={`${inputRound} pl-10`}
                        />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <label className={labelClass}>{t('profile:activityFilterElement')}</label>
                    <div className="relative">
                        <FileText size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={elementSearch}
                            onChange={(e) => setElementSearch(e.target.value)}
                            placeholder={t('profile:activityFilterElementPlaceholder')}
                            className={`${inputRound} pl-10`}
                        />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <label className={labelClass}>{t('profile:activityPeriodLabel')}</label>
                    <div className="flex items-center gap-1 rounded-xl bg-white px-2 shadow-sm dark:bg-slate-900">
                        <Calendar size={16} className="ml-1 flex-shrink-0 text-slate-400" />
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full flex-1 border-none bg-transparent py-2.5 text-sm focus:ring-0 dark:text-slate-200" />
                        <span className="text-slate-300">–</span>
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full flex-1 border-none bg-transparent py-2.5 text-sm focus:ring-0 dark:text-slate-200" />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12 text-[#595c5d]"><Loader2 size={28} className="animate-spin" /></div>
            ) : activity.length === 0 ? (
                <p className="text-sm text-[#595c5d] dark:text-slate-400">{t('profile:activityEmpty')}</p>
            ) : (
                <>
                    <div className="overflow-hidden rounded-2xl bg-white shadow-sm dark:border dark:border-slate-700 dark:bg-slate-800">
                        <div className="grid grid-cols-12 gap-4 border-b border-[#dadddf] bg-[#e0e3e4] px-6 py-4 dark:border-slate-600 dark:bg-slate-700/80">
                            <div className="col-span-4 text-xs font-bold uppercase tracking-widest text-[#595c5d] dark:text-slate-300">{t('profile:activityActionCol')}</div>
                            <div className="col-span-3 text-xs font-bold uppercase tracking-widest text-[#595c5d] dark:text-slate-300">{t('profile:activityProjectCol')}</div>
                            <div className="col-span-3 text-xs font-bold uppercase tracking-widest text-[#595c5d] dark:text-slate-300">{t('profile:activityUserCol')}</div>
                            <div className="col-span-2 text-right text-xs font-bold uppercase tracking-widest text-[#595c5d] dark:text-slate-300">{t('profile:activityDateCol')}</div>
                        </div>
                        <div className="divide-y divide-[#eff1f2] dark:divide-slate-700">
                            {slice.map((a) => (
                                <div key={a.id} className="grid grid-cols-12 items-center gap-4 px-6 py-5 transition-colors hover:bg-[#eff1f2]/80 dark:hover:bg-slate-700/40">
                                    <div className="col-span-4 flex items-center gap-4">
                                        <ActivityTypeIcon summary={a.summary} action={a.action} />
                                        <div className="min-w-0">
                                            <span className="block text-sm font-semibold text-[#2c2f30] dark:text-slate-100">{a.summary || a.action || '—'}</span>
                                            {(a.widget_type || a.widget_id) ? (
                                                <span className="text-xs text-[#595c5d] dark:text-slate-400">
                                                    {t('profile:activityWidget')}: {a.widget_type || '—'}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="col-span-3">
                                        <span className="inline-flex rounded-full bg-[#d6e3fb] px-3 py-1 text-xs font-medium text-[#465365] dark:bg-blue-950/50 dark:text-blue-200">
                                            {a.course_name || '—'}
                                        </span>
                                    </div>
                                    <div className="col-span-3 flex items-center gap-3">
                                        <img src={ATM_ACTIVE_USER_AVATAR} alt="" className="h-6 w-6 flex-shrink-0 rounded-full border border-[#e6e8ea] object-cover dark:border-slate-600" />
                                        <span className="truncate text-sm font-medium text-[#2c2f30] dark:text-slate-200">{a.user_name || a.user_id || '—'}</span>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <span className="block text-sm font-medium text-[#2c2f30] dark:text-slate-200">
                                            {a.created_at ? new Date(a.created_at).toLocaleString(i18n.language === 'pt' || String(i18n.language).startsWith('pt') ? 'pt-BR' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                                        </span>
                                        <span className="text-[10px] font-bold uppercase tracking-tighter text-[#595c5d] dark:text-slate-500">
                                            {a.created_at ? formatRelativeShort(a.created_at, i18n.language) : ''}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between bg-[#eff1f2] px-6 py-4 dark:bg-slate-800/80">
                            <span className="text-xs font-medium text-[#595c5d] dark:text-slate-400">
                                {t('profile:showingChanges', {
                                    from: total === 0 ? 0 : (safePage - 1) * pageSize + 1,
                                    to: Math.min(safePage * pageSize, total),
                                    total,
                                })}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    disabled={safePage <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    className="rounded-lg border-none bg-white px-4 py-2 text-xs font-bold text-[#595c5d] shadow-sm transition-all hover:bg-[#f5f6f7] disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-900 dark:text-slate-300"
                                >
                                    {t('profile:prevPage')}
                                </button>
                                <button
                                    type="button"
                                    disabled={safePage >= totalPages}
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    className="rounded-lg border-none bg-[#0546ed] px-4 py-2 text-xs font-bold text-white shadow-md transition-all hover:bg-[#003cd3] disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    {t('profile:nextPage')}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0546ed] to-[#003cd3] p-6 text-white shadow-xl">
                            <div className="relative z-10">
                                <span className="text-xs font-bold uppercase tracking-widest opacity-80">{t('profile:insightProductivityTitle')}</span>
                                <div className="mt-4">
                                    <span className="text-3xl font-extrabold">{t('profile:insightProductivityValue')}</span>
                                    <p className="mt-1 text-sm opacity-90">{t('profile:insightProductivitySub')}</p>
                                </div>
                            </div>
                            <TrendingUp className="absolute -bottom-6 -right-6 h-32 w-32 text-white opacity-10" strokeWidth={1} />
                        </div>
                        <div className="flex flex-col justify-between rounded-2xl border border-[#e6e8ea] bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-widest text-[#595c5d] dark:text-slate-400">{t('profile:insightTopCollaboratorTitle')}</span>
                                <div className="mt-4 flex items-center gap-4">
                                    <img src={ATM_ACTIVE_USER_AVATAR} alt="" className="h-12 w-12 rounded-full border-2 border-[#0546ed]/20 object-cover" />
                                    <div>
                                        <h4 className="font-bold text-[#2c2f30] dark:text-slate-100">{topUser.name}</h4>
                                        <p className="text-xs text-[#595c5d] dark:text-slate-400">{t('profile:insightTopCollaboratorSub', { count: topUser.count })}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col justify-between rounded-2xl border border-[#e6e8ea] bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-widest text-[#595c5d] dark:text-slate-400">{t('profile:insightInfraTitle')}</span>
                                <div className="mt-4 flex items-center gap-3">
                                    <div className="h-3 w-3 animate-pulse rounded-full bg-[#006947]" />
                                    <span className="font-bold text-[#2c2f30] dark:text-slate-100">{t('profile:insightInfraStatus')}</span>
                                </div>
                                <p className="mt-2 text-xs text-[#595c5d] dark:text-slate-400">{t('profile:insightInfraSub')}</p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export function CollaboratorsPanel({ authToken, user, onOpenProject, onBack }) {
    const { t } = useTranslation(['profile', 'collab']);
    const [projects, setProjects] = React.useState([]);
    const [invites, setInvites] = React.useState([]);
    const [sentInvites, setSentInvites] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [overview, setOverview] = React.useState(null);
    const [overviewLoading, setOverviewLoading] = React.useState(true);
    const [expandedUsers, setExpandedUsers] = React.useState(() => new Set());
    const [inviteEmail, setInviteEmail] = React.useState('');
    const [inviteCanEdit, setInviteCanEdit] = React.useState(false);
    const [inviteCanCreate, setInviteCanCreate] = React.useState(false);
    const [inviteLayoutId, setInviteLayoutId] = React.useState('');
    const [inviting, setInviting] = React.useState(false);
    const [patchingKey, setPatchingKey] = React.useState(null);

    const [resendingId, setResendingId] = React.useState(null);
    const [revokingId, setRevokingId] = React.useState(null);
    const [error, setError] = React.useState(null);
    const [success, setSuccess] = React.useState(null);

    const headers = () => ({
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
    });

    const loadOverview = React.useCallback(() => {
        if (!authToken) return;
        setOverviewLoading(true);
        fetch(`${API}/collaborators-overview`, { headers: headers() })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => setOverview(d))
            .catch(() => setOverview(null))
            .finally(() => setOverviewLoading(false));
    }, [authToken]);

    React.useEffect(() => {
        if (!authToken) return;
        setLoading(true);
        setError(null);
        Promise.all([
            fetch(`${API}/layouts`, { headers: headers() }).then(r => { if (!r.ok) throw new Error('layouts'); return r.json(); }).then(d => d.projects || []),
            fetch(`${API}/invites/pending`, { headers: headers() }).then(r => { if (!r.ok) return { invites: [] }; return r.json(); }).then(d => d.invites || []),
            fetch(`${API}/invites/sent`, { headers: headers() }).then(r => { if (!r.ok) return { invites: [] }; return r.json(); }).then(d => d.invites || []),
        ])
            .then(([projs, invs, sent]) => {
                setProjects(Array.isArray(projs) ? projs : []);
                setInvites(Array.isArray(invs) ? invs : []);
                setSentInvites(Array.isArray(sent) ? sent : []);
            })
            .catch(() => setError(t('collab:loadError')))
            .finally(() => setLoading(false));
    }, [authToken, t]);

    React.useEffect(() => {
        loadOverview();
    }, [loadOverview]);

    const userId = user?.id ?? user?.userId;
    const tenantId = user?.tenant_id ?? user?.tenantId;
    const isNotFolder = (p) => p.type !== 'folder' && p.type !== 'Folder';
    const isOwner = (p) => p.user_id === userId || (tenantId && String(p.tenant_id) === String(tenantId));
    const myProjects = projects.filter(p => isNotFolder(p) && isOwner(p));
    const myShareable = projects.filter(p => isOwner(p));
    const ownedOptions = React.useMemo(() => {
        const raw = overview?.ownedProjectOptions?.length
            ? overview.ownedProjectOptions
            : myShareable.map((p) => ({
                id: p.id,
                course_name: p.course_name,
                type: p.type,
                isFolder: !isNotFolder(p),
            }));
        return [...raw].sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return (a.course_name || '').localeCompare(b.course_name || '', 'pt');
        });
    }, [overview?.ownedProjectOptions, myShareable, isNotFolder]);
    const sharedProjects = projects.filter(p => isNotFolder(p) && !isOwner(p));

    React.useEffect(() => {
        const opts = ownedOptions || [];
        if (!opts.length) return;
        setInviteLayoutId((id) => ((id && opts.some((o) => o.id === id)) ? id : opts[0].id));
    }, [overview, ownedOptions.length]);

    const toggleUserExpanded = (uid) => {
        setExpandedUsers((prev) => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid);
            else next.add(uid);
            return next;
        });
    };

    const rolesLabel = (rolesList) => {
        const parts = (rolesList || []).map((r) => {
            if (r === 'create') return t('collab:permCreate');
            if (r === 'edit') return t('collab:permEdit');
            return t('collab:permView');
        });
        return [...new Set(parts)].join(' · ');
    };

    const formatShareTargetLabel = (item) => {
        if (!item) return '';
        const name = item.course_name || item.id || '';
        if (item.isFolder || item.type === 'folder' || item.type === 'Folder') {
            return `${t('collab:folderPrefix')} ${name}`;
        }
        return name;
    };

    const projPermPreset = (proj) => {
        if (proj?.can_create_projects) return 'edit_create';
        if (proj?.can_edit) return 'edit';
        if (proj?.role === 'editor') return 'edit';
        return 'view';
    };

    const presetToInviteBody = (preset) => {
        if (preset === 'edit_create') return { can_edit: true, can_create_projects: true };
        if (preset === 'edit') return { can_edit: true, can_create_projects: false };
        return { can_edit: false, can_create_projects: false };
    };

    const invitePermSummary = (row) => {
        let c = row?.can_create_projects === true || row?.can_create_projects === 1;
        let e = row?.can_edit === true || row?.can_edit === 1;
        if (row && row.can_edit == null && row.can_create_projects == null && row.role) {
            e = row.role === 'editor';
            c = false;
        }
        if (c) return `${t('collab:permView')} + ${t('collab:permEdit')} + ${t('collab:permCreate')}`;
        if (e) return `${t('collab:permView')} + ${t('collab:permEdit')}`;
        return t('collab:permView');
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim() || !inviteLayoutId || inviting) return;
        setInviting(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await fetch(`${API}/layouts/${inviteLayoutId}/collaborators/invite`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({
                    email: inviteEmail.trim(),
                    can_edit: !!(inviteCanEdit || inviteCanCreate),
                    can_create_projects: !!inviteCanCreate,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Erro ao convidar');
            setSuccess(t('collab:inviteSent'));
            setInviteEmail('');
            const sentRes = await fetch(`${API}/invites/sent`, { headers: headers() }).then(r => r.json()).catch(() => ({}));
            if (Array.isArray(sentRes.invites)) setSentInvites(sentRes.invites);
            loadOverview();
        } catch (err) {
            setError(err.message || t('collab:inviteError'));
        } finally {
            setInviting(false);
        }
    };

    const handleRemove = async (layoutId, targetUserId) => {
        if (!layoutId || !confirm(t('collab:confirmRemove'))) return;
        try {
            const res = await fetch(`${API}/layouts/${layoutId}/collaborators/${targetUserId}`, {
                method: 'DELETE',
                headers: headers(),
            });
            if (!res.ok) throw new Error((await res.json()).error);
            setSuccess(t('collab:removed'));
            loadOverview();
        } catch (err) {
            setError(err.message);
        }
    };

    const handlePermPresetChange = async (layoutId, targetUserId, preset) => {
        const key = `${layoutId}:${targetUserId}`;
        setPatchingKey(key);
        setError(null);
        try {
            const body = presetToInviteBody(preset);
            const res = await fetch(`${API}/layouts/${layoutId}/collaborators/${targetUserId}`, {
                method: 'PATCH',
                headers: headers(),
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || t('collab:roleUpdateError', { defaultValue: 'Não foi possível alterar a função.' }));
            setSuccess(t('collab:roleUpdated', { defaultValue: 'Função atualizada.' }));
            loadOverview();
        } catch (err) {
            setError(err.message);
        } finally {
            setPatchingKey(null);
        }
    };

    const handleAcceptInvite = async (token) => {
        try {
            const res = await fetch(`${API}/invites/${token}/accept`, { method: 'POST', headers: headers() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            setInvites(prev => prev.filter(i => i.token !== token));
            setSuccess(t('collab:inviteAccepted'));
            if (data.layoutId && onOpenProject) onOpenProject(data.layoutId);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeclineInvite = async (token) => {
        try {
            await fetch(`${API}/invites/${token}/decline`, { method: 'POST', headers: headers() });
            setInvites(prev => prev.filter(i => i.token !== token));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleResendInvite = async (inv, layoutId) => {
        const lid = layoutId;
        if (!lid || resendingId) return;
        const key = `${lid}:${inv.email}`;
        setResendingId(key);
        setError(null);
        setSuccess(null);
        try {
            const res = await fetch(`${API}/layouts/${lid}/collaborators/invite`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({
                    email: inv.email,
                    can_edit: !!(inv.can_edit || inv.can_create_projects),
                    can_create_projects: !!inv.can_create_projects,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || t('collab:inviteError'));
            setSuccess(t('collab:inviteResent'));
            const sentRes = await fetch(`${API}/invites/sent`, { headers: headers() }).then(r => r.json()).catch(() => ({}));
            if (Array.isArray(sentRes.invites)) setSentInvites(sentRes.invites);
            loadOverview();
        } catch (err) {
            setError(err.message || t('collab:inviteError'));
        } finally {
            setResendingId(null);
        }
    };

    const handleRevokeInvite = async (inv) => {
        const inviteId = inv?.id;
        if (!inviteId || revokingId) return;
        if (!window.confirm(t('collab:revokeInviteConfirm'))) return;
        setRevokingId(inviteId);
        setError(null);
        setSuccess(null);
        try {
            const res = await fetch(`${API}/invites/sent/${inviteId}`, { method: 'DELETE', headers: headers() });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || t('collab:revokeInviteError'));
            setSuccess(t('collab:inviteRevoked'));
            const sentRes = await fetch(`${API}/invites/sent`, { headers: headers() }).then((r) => r.json()).catch(() => ({}));
            if (Array.isArray(sentRes.invites)) setSentInvites(sentRes.invites);
            loadOverview();
        } catch (err) {
            setError(err.message || t('collab:revokeInviteError'));
        } finally {
            setRevokingId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16 text-slate-500 dark:text-slate-400">
                <Loader2 size={24} className="animate-spin" />
            </div>
        );
    }

    const collabCount = (overview?.collaborators || []).length;

    return (
        <div className={`w-full space-y-10 text-[#2c2f30] dark:text-slate-100 ${ATM_FH}`}>
            {error && <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 text-sm">{error}</div>}
            {success && <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-700 dark:text-emerald-400 text-sm">{success}</div>}

            {ownedOptions.length > 0 && (
                <div className="rounded-xl border border-[#abadae]/10 bg-white p-8 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <div className="mb-6">
                        <h3 className="text-2xl font-extrabold tracking-tight text-[#2c2f30] dark:text-slate-100">{t('collab:inviteSectionTitle')}</h3>
                        <p className="mt-1 text-sm text-[#595c5d] dark:text-slate-400">{t('collab:inviteSectionSub')}</p>
                        <p className="mt-2 text-xs text-[#595c5d]/90 dark:text-slate-500">{t('collab:folderShareHint')}</p>
                    </div>
                    {overview?.limitsExplain && overview?.limitsSummary?.mode && overview.limitsSummary.mode !== 'none' ? (
                        <div className="mb-6 rounded-xl border border-blue-200/90 bg-blue-50/90 p-4 text-sm text-slate-800 shadow-sm dark:border-blue-800 dark:bg-blue-950/35 dark:text-slate-200">
                            <h4 className="mb-2 font-bold text-blue-900 dark:text-blue-200">{t('collab:limitsExplainTitle')}</h4>
                            <p className="mb-3 text-slate-700 dark:text-slate-300">
                                {t('collab:limitsExplainWorkspace', { count: overview.ownedProjectCount ?? ownedOptions.length ?? 0 })}
                            </p>
                            {overview.limitsExplain.mode === 'split' ? (
                                <>
                                    <ul className="list-disc space-y-2 pl-5 text-slate-700 dark:text-slate-300">
                                        <li>
                                            <span className="font-semibold text-slate-900 dark:text-slate-100">{t('collab:limitsExplainEditorsRole')}</span>
                                            {' — '}
                                            {t('collab:limitsExplainEditorsLine', {
                                                cap: overview.limitsExplain.capEditorsWorkspace,
                                                plan: overview.limitsExplain.planEditors,
                                                addon: overview.limitsExplain.addonEditors,
                                            })}
                                        </li>
                                        <li>
                                            <span className="font-semibold text-slate-900 dark:text-slate-100">{t('collab:limitsExplainViewersRole')}</span>
                                            {' — '}
                                            {t('collab:limitsExplainViewersLine', {
                                                cap: overview.limitsExplain.capViewersWorkspace,
                                                plan: overview.limitsExplain.planViewers,
                                                addon: overview.limitsExplain.addonViewers,
                                            })}
                                        </li>
                                    </ul>
                                    <p className="mt-3 border-t border-blue-200/70 pt-3 text-xs text-slate-600 dark:border-blue-800/60 dark:text-slate-400">
                                        {t('collab:limitsExplainUsageSplit', {
                                            usedEd: overview.limitsSummary.editorsUsed,
                                            maxEd: overview.limitsSummary.editorsMax,
                                            usedVw: overview.limitsSummary.viewersUsed,
                                            maxVw: overview.limitsSummary.viewersMax,
                                        })}
                                    </p>
                                </>
                            ) : overview.limitsExplain.mode === 'combined' ? (
                                <>
                                    <p className="text-slate-700 dark:text-slate-300">
                                        {t('collab:limitsExplainCombinedIntro', {
                                            cap: overview.limitsExplain.capWorkspace,
                                            plan: overview.limitsExplain.planTotal,
                                            addon: overview.limitsExplain.addonExtra,
                                        })}
                                    </p>
                                    <p className="mt-3 border-t border-blue-200/70 pt-3 text-xs text-slate-600 dark:border-blue-800/60 dark:text-slate-400">
                                        {t('collab:limitsExplainUsageCombined', {
                                            used: overview.limitsSummary.totalUsed,
                                            max: overview.limitsSummary.totalMax,
                                        })}
                                    </p>
                                </>
                            ) : null}
                        </div>
                    ) : null}
                    <form onSubmit={handleInvite} className="grid grid-cols-1 items-end gap-6 lg:grid-cols-12">
                        <div className="space-y-2 lg:col-span-3">
                            <label className="ml-1 text-xs font-bold uppercase tracking-wider text-[#595c5d] dark:text-slate-400">{t('collab:emailLabel')}</label>
                            <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="exemplo@empresa.com" className="w-full rounded-xl border-none bg-[#eff1f2] px-4 py-3 text-sm focus:ring-2 focus:ring-[#0546ed] dark:bg-slate-900 dark:text-slate-200" required />
                        </div>
                        <div className="space-y-2 lg:col-span-3">
                            <label className="ml-1 text-xs font-bold uppercase tracking-wider text-[#595c5d] dark:text-slate-400">{t('collab:inviteTargetProjectOrFolder')}</label>
                            <select value={inviteLayoutId} onChange={(e) => setInviteLayoutId(e.target.value)} className="w-full appearance-none rounded-xl border-none bg-[#eff1f2] px-4 py-3 text-sm focus:ring-2 focus:ring-[#0546ed] dark:bg-slate-900 dark:text-slate-200">
                                {ownedOptions.map((o) => (
                                    <option key={o.id} value={o.id}>{formatShareTargetLabel(o)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-3 rounded-xl bg-[#eff1f2]/80 px-4 py-3 dark:bg-slate-900/50 lg:col-span-4">
                            <p className="text-xs font-bold uppercase tracking-wider text-[#595c5d] dark:text-slate-400">{t('collab:invitePermissions')}</p>
                            <label className="flex cursor-default items-center gap-2 text-sm text-[#2c2f30] dark:text-slate-200">
                                <input type="checkbox" checked readOnly className="rounded border-slate-300" />
                                <span>{t('collab:permViewAlways')}</span>
                            </label>
                            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#2c2f30] dark:text-slate-200">
                                <input
                                    type="checkbox"
                                    className="rounded border-slate-300"
                                    checked={inviteCanEdit}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        setInviteCanEdit(v);
                                        if (!v) setInviteCanCreate(false);
                                    }}
                                />
                                <span>{t('collab:inviteCanEdit')}</span>
                            </label>
                            <label className={`flex items-center gap-2 text-sm ${inviteCanEdit ? 'cursor-pointer text-[#2c2f30] dark:text-slate-200' : 'cursor-not-allowed opacity-50'}`}>
                                <input
                                    type="checkbox"
                                    className="rounded border-slate-300"
                                    checked={inviteCanCreate}
                                    disabled={!inviteCanEdit}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        setInviteCanCreate(v);
                                        if (v) setInviteCanEdit(true);
                                    }}
                                />
                                <span>{t('collab:inviteCanCreate')}</span>
                            </label>
                        </div>
                        <button
                            type="submit"
                            disabled={inviting}
                            className="rounded-xl bg-gradient-to-br from-[#0546ed] to-[#003cd3] py-3.5 font-bold text-white shadow-lg shadow-[#0546ed]/20 transition-all hover:shadow-[#0546ed]/30 disabled:opacity-50 lg:col-span-2"
                        >
                            {inviting ? <Loader2 size={18} className="mx-auto animate-spin" /> : t('collab:invite')}
                        </button>
                    </form>
                </div>
            )}

            {sentInvites.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/15 rounded-2xl border-2 border-amber-300 dark:border-amber-700 p-6">
                    <h3 className="font-semibold text-amber-900 dark:text-amber-200 mb-3 flex items-center gap-2"><Mail size={22} /> {t('collab:youSentInvites', { defaultValue: 'Convites que você enviou (aguardando)' })}</h3>
                    <ul className="space-y-3">
                        {sentInvites.map(s => (
                            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-amber-800">
                                <div>
                                    <p className="font-semibold text-slate-900 dark:text-slate-100">{formatShareTargetLabel({ course_name: s.course_name, type: s.type, isFolder: s.type === 'folder' || s.type === 'Folder' }) || t('collab:project', { defaultValue: 'Projeto' })}</p>
                                    <p className="text-sm text-slate-600 dark:text-slate-400">{s.email}</p>
                                    <p className="text-xs text-amber-700 dark:text-amber-400">{invitePermSummary(s)} · {t('collab:pending', { defaultValue: 'Aguardando aceite' })}</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleResendInvite(s, s.layout_id)}
                                        disabled={resendingId === `${s.layout_id}:${s.email}` || revokingId === s.id}
                                        className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {resendingId === `${s.layout_id}:${s.email}` ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                                        {t('collab:resend', { defaultValue: 'Reenviar' })}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleRevokeInvite(s)}
                                        disabled={revokingId === s.id || resendingId === `${s.layout_id}:${s.email}`}
                                        className="flex items-center gap-2 px-4 py-2 text-sm border border-red-300 bg-white text-red-700 rounded-lg hover:bg-red-50 dark:border-red-800 dark:bg-slate-800 dark:text-red-300 dark:hover:bg-red-950/40 disabled:opacity-50"
                                    >
                                        {revokingId === s.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                        {t('collab:revokeInvite')}
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {invites.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
                    <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2"><Mail size={20} /> {t('collab:pendingInvites', { defaultValue: 'Convites pendentes' })}</h3>
                    <ul className="space-y-3">
                        {invites.map(inv => (
                            <li key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                <div>
                                    <span className="font-medium text-slate-800 dark:text-slate-200">{inv.course_name}</span>
                                    <span className="text-slate-500 dark:text-slate-400 text-sm ml-2">{t('collab:invitedBy', { defaultValue: 'Convidado por' })} {inv.inviter_name}</span>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleDeclineInvite(inv.token)} className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">{t('collab:decline', { defaultValue: 'Recusar' })}</button>
                                    <button onClick={() => handleAcceptInvite(inv.token)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">{t('collab:accept', { defaultValue: 'Aceitar' })}</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="bg-transparent dark:bg-transparent">
                <div className="mb-6 flex items-center justify-between px-2">
                    <h3 className="text-xl font-bold text-[#2c2f30] dark:text-slate-100">{t('collab:activeCollaborators')}</h3>
                    <span className="rounded-full bg-[#69f6b8] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#005a3c]">
                        {t('collab:membersBadge', { count: collabCount })}
                    </span>
                </div>
                <div className="overflow-hidden rounded-xl border border-[#abadae]/10 bg-[#eff1f2] dark:border-slate-700 dark:bg-slate-800/50">
                <h3 className="sr-only">{t('collab:byPersonTitle', { defaultValue: 'Colaboradores por pessoa' })}</h3>
                {ownedOptions.length === 0 ? (
                    <div className="space-y-2">
                        <p className="text-slate-500 dark:text-slate-400 text-sm">{t('collab:noProjects', { defaultValue: 'Nenhum projeto para gerenciar colaboradores.' })}</p>
                        <p className="text-slate-500 dark:text-slate-400 text-sm">{t('collab:createProjectHint', { defaultValue: 'Crie um projeto no builder primeiro. Depois volte aqui para convidar colaboradores.' })}</p>
                        {onBack && (
                            <button type="button" onClick={onBack} className="text-sm text-blue-600 hover:underline">
                                {t('collab:goToBuilder', { defaultValue: 'Ir para o builder' })}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {overviewLoading ? (
                            <div className="flex justify-center py-8"><Loader2 size={28} className="animate-spin text-slate-400" /></div>
                        ) : (
                            <>
                                {overview?.pendingByProject?.length > 0 && (
                                    <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                                        <h4 className="font-semibold text-amber-900 dark:text-amber-200 mb-3 flex items-center gap-2 text-sm">
                                            <Mail size={18} /> {t('collab:pendingInvitesByProject', { defaultValue: 'Convites pendentes (por projeto)' })}
                                        </h4>
                                        <div className="space-y-4">
                                            {overview.pendingByProject.map((blk) => (
                                                <div key={blk.layoutId}>
                                                    <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">{formatShareTargetLabel(blk)}</p>
                                                    <ul className="space-y-2">
                                                        {blk.invites.map((inv) => (
                                                            <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 p-2 bg-white dark:bg-slate-800/60 rounded-lg border border-amber-200/50 dark:border-amber-800/50 text-sm">
                                                                <span className="text-slate-700 dark:text-slate-300">{inv.email} · {invitePermSummary(inv)}</span>
                                                                <div className="flex flex-wrap gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleResendInvite(inv, blk.layoutId)}
                                                                        disabled={resendingId === `${blk.layoutId}:${inv.email}` || revokingId === inv.id}
                                                                        className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                                                    >
                                                                        {resendingId === `${blk.layoutId}:${inv.email}` ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                                                                        {t('collab:resend')}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRevokeInvite(inv)}
                                                                        disabled={revokingId === inv.id || resendingId === `${blk.layoutId}:${inv.email}`}
                                                                        className="flex items-center gap-1 px-2 py-1 text-xs border border-red-300 text-red-700 dark:border-red-800 dark:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50"
                                                                    >
                                                                        {revokingId === inv.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                                                        {t('collab:revokeInvite')}
                                                                    </button>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {(overview?.collaborators || []).length === 0 ? (
                                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">{t('collab:noCollaboratorsYet', { defaultValue: 'Nenhum colaborador ainda. Use o formulário abaixo para convidar.' })}</p>
                                ) : (
                                    <ul className="space-y-2 mb-6">
                                        {overview.collaborators.map((c) => {
                                            const open = expandedUsers.has(c.userId);
                                            return (
                                                <li key={c.userId} className="border border-slate-200 dark:border-slate-600 rounded-xl overflow-hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleUserExpanded(c.userId)}
                                                        className="w-full flex items-center justify-between gap-3 p-3 text-left bg-slate-50 dark:bg-slate-700/40 hover:bg-slate-100 dark:hover:bg-slate-700/70 transition-colors"
                                                    >
                                                        <div className="min-w-0">
                                                            <span className="font-medium text-slate-800 dark:text-slate-200">{c.name}</span>
                                                            <span className="text-slate-500 dark:text-slate-400 text-sm block truncate">{c.email}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-xs px-2 py-0.5 rounded-md bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 max-w-[10rem] truncate" title={rolesLabel(c.rolesList)}>
                                                                {rolesLabel(c.rolesList)}
                                                            </span>
                                                            <ChevronRight size={18} className={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
                                                        </div>
                                                    </button>
                                                    {open && (
                                                        <ul className="border-t border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/80 p-3 space-y-2">
                                                            {c.projects.map((proj) => {
                                                                const pk = `${proj.layoutId}:${c.userId}`;
                                                                const busy = patchingKey === pk;
                                                                return (
                                                                    <li key={`${proj.layoutId}-${projPermPreset(proj)}`} className="flex flex-wrap items-center gap-2 justify-between p-2 rounded-lg border border-slate-100 dark:border-slate-700/80">
                                                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                            {proj.can_create_projects ? <Pencil size={16} className="text-violet-500 flex-shrink-0" /> : proj.can_edit ? <Pencil size={16} className="text-blue-500 flex-shrink-0" /> : <Eye size={16} className="text-amber-600 flex-shrink-0" />}
                                                                            <span className="font-medium text-slate-800 dark:text-slate-200 truncate">
                                                                                {formatShareTargetLabel(proj)}
                                                                                {proj.isFolder ? (
                                                                                    <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">
                                                                                        {t('collab:folderIncludesChildren')}
                                                                                    </span>
                                                                                ) : null}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                                            <select
                                                                                value={projPermPreset(proj)}
                                                                                disabled={busy}
                                                                                onChange={(e) => handlePermPresetChange(proj.layoutId, c.userId, e.target.value)}
                                                                                className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                                                                                aria-label={t('collab:changeRole', { defaultValue: 'Alterar permissões' })}
                                                                            >
                                                                                <option value="view">{t('collab:permPresetView')}</option>
                                                                                <option value="edit">{t('collab:permPresetEdit')}</option>
                                                                                <option value="edit_create">{t('collab:permPresetEditCreate')}</option>
                                                                            </select>
                                                                            {busy ? <Loader2 size={16} className="animate-spin text-slate-400" /> : null}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleRemove(proj.layoutId, c.userId)}
                                                                                className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md border border-red-200 dark:border-red-900/50"
                                                                            >
                                                                                {t('collab:remove')}
                                                                            </button>
                                                                        </div>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                            </>
                        )}
                    </>
                )}
                </div>
            </div>
            {sharedProjects.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
                    <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-4">{t('collab:sharedWithMe', { defaultValue: 'Projetos compartilhados comigo' })}</h3>
                    <ul className="space-y-2">
                        {sharedProjects.map(proj => (
                            <li key={proj.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                <span className="font-medium text-slate-800 dark:text-slate-200">{proj.course_name || 'Sem título'}</span>
                                {onOpenProject && <button onClick={() => onOpenProject(proj.id)} className="text-sm text-blue-600 hover:underline">{t('collab:openProject', { defaultValue: 'Abrir' })}</button>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-[#abadae]/10 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <Asterisk className="mb-2 h-6 w-6 text-[#0546ed]" />
                    <p className="text-3xl font-black text-[#2c2f30] dark:text-slate-100">85%</p>
                    <p className="mt-1 text-xs font-bold uppercase tracking-widest text-[#595c5d] dark:text-slate-400">{t('collab:statCollabRate')}</p>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-[#0546ed] to-[#003cd3] p-6 text-white shadow-lg">
                    <Zap className="mb-2 h-6 w-6 opacity-95" />
                    <p className="text-3xl font-black">24h</p>
                    <p className="mt-1 text-xs font-bold uppercase tracking-widest opacity-90">{t('collab:statAvgResponse')}</p>
                </div>
                <div className="rounded-xl border border-[#abadae]/10 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <ShieldCheck className="mb-2 h-6 w-6 text-[#006947]" />
                    <p className="text-3xl font-black text-[#2c2f30] dark:text-slate-100">{t('collab:statSecureValue')}</p>
                    <p className="mt-1 text-xs font-bold uppercase tracking-widest text-[#595c5d] dark:text-slate-400">{t('collab:statSecureLabel')}</p>
                </div>
            </div>
            <LegalFooter className="mt-8 border-t-0 pt-6 text-[10px] uppercase tracking-widest text-[#9b9d9e] md:normal-case md:tracking-normal md:text-sm" />
        </div>
    );
}

export function TemplatesPanel({ onApply, onPreview, buildTemplateGalleryPreviewSrcDoc, suppressTemplateGalleryEscape }) {
    const { t } = useTranslation('builder');
    const [templates, setTemplates] = React.useState([]);
    const [gallery, setGallery] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [galleryOpen, setGalleryOpen] = React.useState(false);

    React.useEffect(() => {
        setError(null);
        Promise.all([
            fetch(`${API}/templates`).then(r => { if (!r.ok) throw new Error('Templates: ' + r.status); return r.json(); }).then(d => d.templates || []),
            fetch(`${API}/gallery`).then(r => { if (!r.ok) throw new Error('Galeria: ' + r.status); return r.json(); }).then(d => d.items || [])
        ]).then(([tpl, gal]) => {
            setTemplates(Array.isArray(tpl) ? tpl : []);
            setGallery(Array.isArray(gal) ? gal : []);
        }).catch(() => {
            setError('templatesError');
        }).finally(() => setLoading(false));
    }, []);

    const totalCount = templates.length + gallery.length;

    if (loading) return <div className="p-6 text-center text-slate-500 dark:text-slate-400">{t('loading')}</div>;
    if (error) return <div className="p-6 text-center text-red-600 dark:text-red-400 text-sm">{t(error)}</div>;

    return (
        <>
            <div className="space-y-4">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('templatesHint')}
                </p>

                {totalCount > 0 ? (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800/50 p-4 space-y-3">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
                            <span>
                                {t('templatesOfficial')}:{' '}
                                <strong className="text-slate-900 dark:text-slate-100">{templates.length}</strong>
                            </span>
                            <span>
                                {t('templatesCommunity')}:{' '}
                                <strong className="text-slate-900 dark:text-slate-100">{gallery.length}</strong>
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setGalleryOpen(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm shadow-md transition-colors"
                        >
                            <LayoutTemplate size={20} />
                            {t('templatesGalleryOpen', { defaultValue: 'Abrir galeria de templates' })}
                        </button>
                    </div>
                ) : (
                    <div className="text-center py-8 text-slate-400">
                        <LayoutTemplate size={40} className="mx-auto mb-3 text-slate-300" />
                        <p className="text-sm">{t('noTemplates')}</p>
                    </div>
                )}
            </div>
            <TemplatesGalleryModal
                open={galleryOpen}
                onClose={() => setGalleryOpen(false)}
                templates={templates}
                gallery={gallery}
                loading={false}
                errorKey={null}
                onApply={onApply}
                buildTemplateGalleryPreviewSrcDoc={buildTemplateGalleryPreviewSrcDoc}
                suppressEscapeClose={!!suppressTemplateGalleryEscape}
                onPreview={(item) => {
                    onPreview?.(item);
                }}
            />
        </>
    );
}
